import React, { useState, useEffect, useRef, useCallback } from "react";
import { VoiceVisualizer } from "./AudioVisualizer";
import { BsMicFill, BsMicMuteFill } from "react-icons/bs";
import { IoArrowBack } from "react-icons/io5";
import {
  HiOutlineChat,
  HiPhone,
  HiOutlineShare,
  HiCheck,
} from "react-icons/hi";
import { FaHistory } from "react-icons/fa";
import { MdOutlineAutorenew } from "react-icons/md";
import { IoMdClose, IoMdExpand, IoMdContract } from "react-icons/io";
import { IconWrapper } from "./IconWrapper";
import {
  API_BASE_URL,
  AGENT_ENDPOINTS,
  LANGUAGE_ENDPOINTS,
  ADMIN_AGENT_ENDPOINTS,
} from "../../config/api";
import {
  AgentSummary,
  AgentRuntimeFields,
  DirectCallTraceEvent,
  DirectCallProviderCheck,
  LanguageMapping,
  ProviderDebug,
  checkPhoneCallProviders,
  getPhoneCallProviderDebug,
  initiatePhoneCall,
} from "../../services/api";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { Modal } from "../Common/Modal";
import { validateIndianPhone, formatIndianPhone } from "../../utils/validation";
import PhoneInput from "../Common/PhoneInput";
import { motion } from "framer-motion";
import {
  formatDirectCallTraceDuration,
  formatDirectCallTraceTimestamp,
  getDirectCallTraceSeverity,
  groupDirectCallTraceEvents,
  summarizeDirectCallTrace,
  summarizeDirectCallTraceTurn,
} from "./directCallTrace";

// Cost constants for Gemini Live API (in USD per 1M tokens)
const COST_CONSTANTS = {
  // Gemini costs
  GEMINI_INPUT_TEXT: 0.5,
  GEMINI_INPUT_AUDIO: 1,
  GEMINI_OUTPUT_TEXT: 2.5,
  GEMINI_OUTPUT_AUDIO: 12.5,
  USD_TO_INR: 86, // 1 USD = 86 INR

  // Fixed costs (in INR)
  FIXED_CONNECTION_COST: 2, // Fixed cost per connection (INR)
  CALLING_SERVICE_COST_PER_MIN: 1, // Calling service cost per minute (INR)
  SERVICE_CHARGE_PER_MIN: 0.3, // Service charge in INR per minute (flat rate)
};

const PHONE_CALL_CLIENT_COOLDOWN_MS = 90_000;

interface AgentAccessResponse {
  remaining_uses: number | null;
  agent_info: AgentSummary;
  access_token?: string;
}

interface AudioAgentProps {
  apiPath?: string;
  onBackToSelection?: () => void;
  onConnectionChange?: (connected: boolean) => void;
}

// Define token usage interface
interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  input_token_details: {
    text: number;
    audio: number;
    cached: number;
    cached_text: number;
    cached_audio: number;
  };
  output_token_details: {
    text: number;
    audio: number;
  };
}

// Define chat message interface
interface ChatMessage {
  type: "user" | "agent";
  text: string;
  timestamp: number;
}

const formatAgentTranscript = (text: string): string => {
  const document = new DOMParser().parseFromString(
    `<div>${text}</div>`,
    "text/html"
  );
  document.querySelectorAll("break").forEach((element) => {
    element.replaceWith(" ");
  });
  return (document.body.textContent || text).replace(/\s+/g, " ").trim();
};

interface RuntimeInfoEntry {
  id: string;
  key: string;
  value: string;
}

interface RuntimeFieldState {
  directFields: Record<string, string>;
  directFieldOrder: string[];
  directFieldDefaults: Record<string, string>;
  extraInfos: RuntimeInfoEntry[];
}

interface PreviousCallValues {
  phoneNumber: string;
  directFields: Record<string, string>;
  extraInfos: RuntimeInfoEntry[];
}

const USER_DETAILS_KEY = "USER_DETAILS";
const FIELD_ORDER_METADATA_KEY = "__FIELD_ORDER";

// Declare the global window interface extension
declare global {
  interface Window {
    updateRemainingUses?: (uses: number | null) => void;
  }
}

const AudioAgent: React.FC<AudioAgentProps> = ({
  apiPath,
  onBackToSelection,
  onConnectionChange,
}) => {
  const { uuid: paramAgentUuid } = useParams<{ uuid: string }>();
  const agentUuid = paramAgentUuid || "";
  const navigate = useNavigate();

  const [agent, setAgent] = useState<AgentSummary | null>(null);
  const [agentAccessToken, setAgentAccessToken] = useState<string | undefined>(
    undefined
  );
  const [remainingUses, setRemainingUses] = useState<number | null>(null);
  const [loadingAgent, setLoadingAgent] = useState<boolean>(true);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [outputStream, setOutputStream] = useState<MediaStream | null>(null);
  const [audioActive, setAudioActive] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [providerDebug, setProviderDebug] = useState<ProviderDebug | null>(null);
  const [providerDebugSessionId, setProviderDebugSessionId] = useState<string | null>(null);
  const [providerTraceEvents, setProviderTraceEvents] = useState<
    DirectCallTraceEvent[]
  >([]);
  const [expandedTraceTurn, setExpandedTraceTurn] = useState<number | null>(null);
  const [providerCheck, setProviderCheck] =
    useState<DirectCallProviderCheck | null>(null);
  const latestProviderCheckRequestKeyRef = useRef<string | null>(null);
  const [languageMappings, setLanguageMappings] = useState<Map<string, string>>(
    new Map()
  );

  // Live chat UI state
  const [chatExpanded, setChatExpanded] = useState<boolean>(false);
  const [showChat, setShowChat] = useState<boolean>(true);

  // Live chat messages
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

  // Token usage tracking
  const [tokenUsage, setTokenUsage] = useState<TokenUsage>({
    input_tokens: 0,
    output_tokens: 0,
    input_token_details: {
      text: 0,
      audio: 0,
      cached: 0,
      cached_text: 0,
      cached_audio: 0,
    },
    output_token_details: {
      text: 0,
      audio: 0,
    },
  });

  // Additional costs tracking
  const [callDurationMinutes, setCallDurationMinutes] = useState<number>(0);
  const [fixedCostsApplied, setFixedCostsApplied] = useState<boolean>(false);

  // Calculated costs
  const [costs, setCosts] = useState({
    usd: 0,
    inr: 0,
    fixedCost: 0,
    callingServiceCost: 0,
    serviceCharge: 0,
    totalInr: 0,
  });

  // Show cost breakdown modal
  const [showCostModal, setShowCostModal] = useState<boolean>(false);
  // Session timer
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [sessionDuration, setSessionDuration] = useState<number>(0);
  const timerIntervalRef = useRef<number | null>(null);

  // Phone call mode states
  const [phoneCallMode, setPhoneCallMode] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const phoneCallCooldownsRef = useRef<Record<string, number>>({});
  const [phoneCallCooldownUntil, setPhoneCallCooldownUntil] =
    useState<number>(0);
  const [phoneCallDirectFields, setPhoneCallDirectFields] = useState<
    Record<string, string>
  >({});
  const [directFieldOrder, setDirectFieldOrder] = useState<string[]>([]);
  const [directFieldDefaults, setDirectFieldDefaults] = useState<
    Record<string, string>
  >({});
  const [phoneCallExtraInfos, setPhoneCallExtraInfos] = useState<
    RuntimeInfoEntry[]
  >([]);
  const [previousCallValues, setPreviousCallValues] =
    useState<PreviousCallValues | null>(null);
  const [phoneNumberPlaceholder, setPhoneNumberPlaceholder] =
    useState<string>("");
  const [isPhoneCallProcessing, setIsPhoneCallProcessing] =
    useState<boolean>(false);
  const [phoneCallMessage, setPhoneCallMessage] = useState<{
    type: "success" | "error";
    text: string;
    title?: string;
  } | null>(null);
  const [phoneValidationMsg, setPhoneValidationMsg] = useState<string | null>(
    null
  );

  // Phone call warning
  const [showPhoneCallWarning, setShowPhoneCallWarning] =
    useState<boolean>(false);

  // Add state for out of uses modal
  const [showOutOfUsesModal, setShowOutOfUsesModal] = useState<boolean>(false);
  const [showBrowserTryModal, setShowBrowserTryModal] = useState<boolean>(false);
  const [browserDirectFields, setBrowserDirectFields] = useState<
    Record<string, string>
  >({});
  const [browserExtraInfos, setBrowserExtraInfos] = useState<RuntimeInfoEntry[]>(
    []
  );
  const [isBrowserTryStarting, setIsBrowserTryStarting] =
    useState<boolean>(false);

  // Share state
  const [linkCopied, setLinkCopied] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const webrtcIdRef = useRef<string>("");
  const streamRef = useRef<MediaStream | null>(null);
  const rawInputStreamRef = useRef<MediaStream | null>(null);

  // Web Audio refs for mic processing and remote analysis
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const inputDestinationNodeRef =
    useRef<MediaStreamAudioDestinationNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  // Keep track if mic gain is lowered
  const micLowRef = useRef<boolean>(false);

  const isAdmin = localStorage.getItem("userType") === "admin";
  // Auto-scroll to the bottom when new messages are added
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!providerDebugSessionId) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 400;

    const pollProviderDebug = async () => {
      if (cancelled || attempts >= maxAttempts) return;
      attempts += 1;
      try {
        const result = await getPhoneCallProviderDebug(providerDebugSessionId);
        if (cancelled) return;
        if (result.ready && result.provider_debug) {
          setProviderDebug(result.provider_debug);
        }
        if (Array.isArray(result.events)) {
          setProviderTraceEvents(result.events);
        }
      } catch (error) {
        console.warn("Provider debug polling failed:", error);
      }
      if (!cancelled) {
        window.setTimeout(pollProviderDebug, 1500);
      }
    };

    pollProviderDebug();

    return () => {
      cancelled = true;
    };
  }, [providerDebugSessionId]);

  const createRuntimeInfoEntry = useCallback(
    (key = "", value = ""): RuntimeInfoEntry => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      key,
      value,
    }),
    []
  );

  const getDefaultRuntimeFieldState = useCallback(
    (agentInfo: AgentSummary | null | undefined): RuntimeFieldState => {
      const rawFields = (agentInfo?.fields || {}) as Record<string, unknown>;
      const directFields: Record<string, string> = {};
      const directFieldOrder: string[] = [];
      const directFieldDefaults: Record<string, string> = {};
      const extraInfos: RuntimeInfoEntry[] = [];
      const explicitOrderRaw = rawFields[FIELD_ORDER_METADATA_KEY];
      const explicitOrder: string[] = [];

      if (Array.isArray(explicitOrderRaw)) {
        const seenExplicit = new Set<string>();
        explicitOrderRaw.forEach((entry) => {
          const normalized = (entry == null ? "" : String(entry)).trim();
          if (
            !normalized ||
            normalized === USER_DETAILS_KEY ||
            normalized.startsWith("__") ||
            seenExplicit.has(normalized)
          ) {
            return;
          }
          explicitOrder.push(normalized);
          seenExplicit.add(normalized);
        });
      }

      Object.entries(rawFields).forEach(([key, value]) => {
        const trimmedKey = key.trim();
        if (!trimmedKey || trimmedKey.startsWith("__")) return;

        if (
          trimmedKey === USER_DETAILS_KEY &&
          value &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          Object.entries(value as Record<string, unknown>).forEach(
            ([detailKey, detailValue]) => {
              const normalizedDetailKey = detailKey.trim();
              if (!normalizedDetailKey) return;
              extraInfos.push(
                createRuntimeInfoEntry(
                  normalizedDetailKey,
                  detailValue == null ? "" : String(detailValue)
                )
              );
            }
          );
          return;
        }

        const normalizedValue = value == null ? "" : String(value);
        directFields[trimmedKey] = normalizedValue;
        directFieldDefaults[trimmedKey] = normalizedValue;
        directFieldOrder.push(trimmedKey);
      });

      let resolvedOrder = [...directFieldOrder];
      if (explicitOrder.length > 0) {
        const orderedKnown = explicitOrder.filter((key) =>
          Object.prototype.hasOwnProperty.call(directFields, key)
        );
        const remainingKnown = directFieldOrder.filter(
          (key) => !orderedKnown.includes(key)
        );
        resolvedOrder = [...orderedKnown, ...remainingKnown];
      }

      return {
        directFields,
        directFieldOrder: resolvedOrder,
        directFieldDefaults,
        extraInfos,
      };
    },
    [createRuntimeInfoEntry]
  );

  const applyPhoneRuntimeDefaults = useCallback(
    (fieldState: RuntimeFieldState): RuntimeFieldState => {
      const directFields = { ...fieldState.directFields };
      const directFieldDefaults = { ...fieldState.directFieldDefaults };
      const directFieldOrder = [...fieldState.directFieldOrder];
      const defaultBotName = "Priya";
      const existingBotName = (directFields.bot_name || "").trim();

      directFields.bot_name = existingBotName || defaultBotName;
      if (!(directFieldDefaults.bot_name || "").trim()) {
        directFieldDefaults.bot_name = directFields.bot_name;
      }
      if (!directFieldOrder.includes("bot_name")) {
        directFieldOrder.unshift("bot_name");
      }

      return {
        ...fieldState,
        directFields,
        directFieldDefaults,
        directFieldOrder,
      };
    },
    []
  );

  const buildRuntimeFieldsPayload = useCallback(
    (
      directFields: Record<string, string>,
      extraInfos: RuntimeInfoEntry[],
      options?: {
        orderedKeys?: string[];
        fallbackDefaults?: Record<string, string>;
      }
    ): AgentRuntimeFields => {
      const payload: AgentRuntimeFields = {};
      const orderedKeys = options?.orderedKeys || [];
      const seen = new Set<string>();
      const keysToUse: string[] = [];

      orderedKeys.forEach((key) => {
        if (
          Object.prototype.hasOwnProperty.call(directFields, key) &&
          !seen.has(key)
        ) {
          keysToUse.push(key);
          seen.add(key);
        }
      });

      Object.keys(directFields).forEach((key) => {
        if (!seen.has(key)) {
          keysToUse.push(key);
          seen.add(key);
        }
      });

      let hasHandledFirstDirectField = false;
      keysToUse.forEach((key) => {
        const trimmedKey = key.trim();
        if (!trimmedKey) return;

        const rawValue = directFields[key] ?? "";
        let resolvedValue = rawValue;

        if (!hasHandledFirstDirectField) {
          if (rawValue.trim() === "") {
            resolvedValue =
              options?.fallbackDefaults?.[key] ??
              options?.fallbackDefaults?.[trimmedKey] ??
              "";
          }
          hasHandledFirstDirectField = true;
        }

        payload[trimmedKey] = resolvedValue;
      });

      const userDetails: Record<string, string> = {};
      extraInfos.forEach((entry) => {
        const trimmedKey = entry.key.trim();
        if (!trimmedKey) return;
        userDetails[trimmedKey] = entry.value ?? "";
      });

      if (Object.keys(userDetails).length > 0) {
        payload[USER_DETAILS_KEY] = userDetails;
      }

      return payload;
    },
    []
  );

  const getOrderedDirectFieldKeys = useCallback(
    (directFields: Record<string, string>): string[] => {
      const knownKeys = directFieldOrder.filter((key) =>
        Object.prototype.hasOwnProperty.call(directFields, key)
      );
      const extraKeys = Object.keys(directFields).filter(
        (key) => !knownKeys.includes(key)
      );
      return [...knownKeys, ...extraKeys];
    },
    [directFieldOrder]
  );

  const phoneDirectFieldKeys = getOrderedDirectFieldKeys(phoneCallDirectFields);
  const browserDirectFieldKeys = getOrderedDirectFieldKeys(browserDirectFields);

  const formatRuntimeFieldLabel = useCallback((key: string): string => {
    return key
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  const applyPreviousCallValues = useCallback(() => {
    if (!previousCallValues) return;

    setPhoneNumber(previousCallValues.phoneNumber);
    setPhoneCallDirectFields({ ...previousCallValues.directFields });
    setPhoneCallExtraInfos(
      previousCallValues.extraInfos.map((entry) => ({
        ...entry,
        id: `${entry.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }))
    );
    setPhoneValidationMsg(null);
    setPhoneCallMessage(null);
    setProviderCheck(null);
    latestProviderCheckRequestKeyRef.current = null;
  }, [previousCallValues]);

  const resetProviderCheck = useCallback(() => {
    setProviderCheck(null);
    latestProviderCheckRequestKeyRef.current = null;
  }, []);

  const buildProviderCheckRequestKey = useCallback(
    (formattedPhone: string, runtimePayload: AgentRuntimeFields): string =>
      JSON.stringify({
        phone: formattedPhone,
        agentUuid,
        fields: runtimePayload,
      }),
    [agentUuid]
  );

  // Check if screen is mobile using useEffect to avoid hydration issues
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint is 1024px
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Set default chat state based on screen size
  useEffect(() => {
    // On desktop: always show expanded chat
    // On mobile: hide chat initially, show button
    setShowChat(!isMobile);
    setChatExpanded(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Clear chat messages when disconnecting
  useEffect(() => {
    if (!isConnected) {
      // Don't clear messages immediately so user can still see them after disconnecting
      // Only clear when connecting again
      if (isConnecting) {
        setChatMessages([]);
      }
    }
  }, [isConnecting, isConnected]);

  // Timer display component
  const TimerDisplay = () => {
    // Force rerenders every second using a counter
    const [, forceUpdate] = useState({});

    useEffect(() => {
      const timer = setInterval(() => {
        forceUpdate({});
      }, 500);

      return () => clearInterval(timer);
    }, []);

    return (
      <div className="bg-[var(--card)] text-[var(--foreground)] font-semibold px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 border border-[var(--border)] shadow-sm">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="min-w-[40px] inline-block text-center">
          {formatDuration(sessionDuration)}
        </span>
      </div>
    );
  };

  // Add this component to force updates for modal session duration display
  const SessionDurationDisplay = () => {
    // Force rerenders every second
    const [, forceUpdate] = useState({});

    useEffect(() => {
      const timer = setInterval(() => {
        forceUpdate({});
      }, 500);

      // Clean up the interval when component unmounts
      return () => clearInterval(timer);
    }, []);

    return (
      <p className="text-gray-900 font-medium">
        {formatDuration(sessionDuration)}
      </p>
    );
  };

  // Fetch language mappings
  useEffect(() => {
    const fetchLanguageMappings = async () => {
      try {
        const response = await api.get(LANGUAGE_ENDPOINTS.MAPPINGS);
        const mappingsData: LanguageMapping[] = response.data;
        const mappings = new Map<string, string>();
        mappingsData.forEach((mapping) => {
          mappings.set(mapping.language_code, mapping.language);
        });
        setLanguageMappings(mappings);
      } catch (error) {
        console.error("Error fetching language mappings:", error);
      }
    };

    fetchLanguageMappings();
  }, []);

  // First thing: validate agent access and redirect immediately if invalid
  useEffect(() => {
    const validateAgentAccess = async () => {
      if (!agentUuid) {
        // No agent UUID means direct redirect
        if (onBackToSelection) {
          onBackToSelection();
        } else {
          navigate("/");
        }
        return;
      }

      try {
        setLoadingAgent(true);

        const response = await api.post(AGENT_ENDPOINTS.ACCESS(agentUuid));
        const data: AgentAccessResponse = response.data;

        // Log access token received from the API
        console.log("Access token received from API:", data.access_token);
        console.log(
          "Access token from agent_info:",
          data.agent_info?.access_token
        );

        // Valid agent, set the data
        setAgent(data.agent_info);
        const phoneDefaults = applyPhoneRuntimeDefaults(
          getDefaultRuntimeFieldState(data.agent_info)
        );
        const browserDefaults = getDefaultRuntimeFieldState(data.agent_info);
        setDirectFieldOrder(phoneDefaults.directFieldOrder);
        setDirectFieldDefaults(phoneDefaults.directFieldDefaults);
        setPhoneCallDirectFields(phoneDefaults.directFields);
        setPhoneCallExtraInfos(phoneDefaults.extraInfos);
        setBrowserDirectFields(browserDefaults.directFields);
        setBrowserExtraInfos(browserDefaults.extraInfos);
        // Store access token from agent_info if provided
        setAgentAccessToken(data.agent_info?.access_token);

        // Extract phone number from token data if available
        try {
          const token = localStorage.getItem("token");
          if (token) {
            const tokenPayload = JSON.parse(atob(token.split(".")[1]));
            if (tokenPayload && tokenPayload.phone) {
              // Remove +91 prefix if present
              const phoneNum = tokenPayload.phone.replace(/^\+91/, "");
              setPhoneNumberPlaceholder(phoneNum);
            }
          }
        } catch (err) {
          console.error("Error extracting phone number from token:", err);
        }

        setRemainingUses(data.remaining_uses);
        // Also update in localStorage for the navbar
        if (data.remaining_uses !== null && data.remaining_uses !== undefined) {
          localStorage.setItem("remainingUses", data.remaining_uses.toString());
          // Update the global counter
          if (window.updateRemainingUses) {
            window.updateRemainingUses(data.remaining_uses);
          }
        }
        setLoadingAgent(false);
      } catch (error: any) {
        console.error("Error validating agent access:", error);

        // Check if it's a 401 Unauthorized error (out of uses)
        if (error.response && error.response.status === 401) {
          // Show the out of uses modal
          setShowOutOfUsesModal(true);
          setLoadingAgent(false);
          // Don't redirect immediately, let the user see the modal
          return;
        }

        // For other errors, redirect
        if (onBackToSelection) {
          onBackToSelection();
        } else {
          navigate("/");
        }
      }
    };

    // Make this the first thing that happens
    validateAgentAccess();
  }, [
    agentUuid,
    navigate,
    onBackToSelection,
    getDefaultRuntimeFieldState,
    applyPhoneRuntimeDefaults,
  ]);

  // Get language name from code
  const getLanguageName = (code: string): string => {
    return languageMappings.get(code) || code;
  };

  // Notify parent of connection state changes
  useEffect(() => {
    onConnectionChange?.(isConnected);
  }, [isConnected, onConnectionChange]);

  // Define a decay duration in milliseconds (e.g., 500ms)
  const DECAY_DURATION = 500;
  // Track the last time when the remote output was above threshold
  let lastActiveTime = performance.now();

  const checkRemoteOutputLevel = () => {
    if (remoteAnalyserRef.current && gainNodeRef.current) {
      const analyser = remoteAnalyserRef.current;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(dataArray);
      // Calculate simple average volume
      const avg =
        dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
      const threshold = 10; // adjust this threshold as needed
      const now = performance.now();

      if (avg > threshold) {
        // Record the current time as the last moment of activity.
        lastActiveTime = now;
        // Lower mic gain if not already lowered
        if (!micLowRef.current) {
          gainNodeRef.current.gain.value = 0.3; // Lower gain value
          micLowRef.current = true;
        }
      } else if (micLowRef.current) {
        // Only restore gain if enough time has passed without activity.
        if (now - lastActiveTime > DECAY_DURATION) {
          gainNodeRef.current.gain.value = 1.0;
          micLowRef.current = false;
        }
      }
    }
    animationFrameRef.current = requestAnimationFrame(checkRemoteOutputLevel);
  };

  // Setup remote analyser for the remote output stream
  const setupRemoteAnalyser = (remoteStream: MediaStream) => {
    // Removed as of now as we are not using standard mode
    return;
    const ac = audioContextRef.current || new AudioContext();
    if (!audioContextRef.current) {
      audioContextRef.current = ac;
    }
    const remoteSource = ac.createMediaStreamSource(remoteStream);
    const analyser = ac.createAnalyser();
    analyser.fftSize = 256; // smaller fftSize for lower latency in analysis
    remoteSource.connect(analyser);
    remoteAnalyserRef.current = analyser;
    // Start monitoring
    if (!animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(checkRemoteOutputLevel);
    }
  };

  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  const releaseMicrophone = useCallback(() => {
    rawInputStreamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current?.getTracks().forEach((track) => track.stop());
    inputSourceNodeRef.current?.disconnect();
    gainNodeRef.current?.disconnect();
    inputDestinationNodeRef.current?.disconnect();
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      void audioContextRef.current.close();
    }
    rawInputStreamRef.current = null;
    streamRef.current = null;
    inputSourceNodeRef.current = null;
    inputDestinationNodeRef.current = null;
    gainNodeRef.current = null;
    audioContextRef.current = null;
    setStream(null);
    setIsMuted(false);
  }, []);

  // Keep the browser microphone track direct; Web Audio destinations can
  // produce a silent outbound track on some browser/device combinations.
  const setupMicrophone = async (): Promise<MediaStream | null> => {
    try {
      releaseMicrophone();
      // Get the microphone stream
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });
      rawInputStreamRef.current = micStream;
      const audioTrack = micStream.getAudioTracks()[0];
      if (!audioTrack || audioTrack.readyState !== "live") {
        throw new Error("Microphone did not provide a live audio track");
      }
      audioTrack.enabled = true;
      streamRef.current = micStream;
      setStream(micStream);
      setErrorMessage("");
      return micStream;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      releaseMicrophone();
      setErrorMessage(`Failed to access microphone: ${errorMsg}`);
      console.error("Microphone setup failed:", errorMsg);
      return null;
    }
  };

  // Add this effect to ensure timer updates properly and track call duration
  useEffect(() => {
    // Only start the timer when both sessionStartTime exists AND we're connected
    if (isConnected && !timerIntervalRef.current) {
      // Reset the session start time to now when we actually get connected
      const newStartTime = Date.now();
      setSessionStartTime(newStartTime);
      // Reset duration to zero
      setSessionDuration(0);
      setCallDurationMinutes(0);

      // Apply fixed costs when connection is established
      if (!fixedCostsApplied) {
        setCosts((prevCosts) => ({
          ...prevCosts,
          fixedCost: COST_CONSTANTS.FIXED_CONNECTION_COST,
          totalInr: prevCosts.inr + COST_CONSTANTS.FIXED_CONNECTION_COST,
          totalUsd:
            (prevCosts.inr + COST_CONSTANTS.FIXED_CONNECTION_COST) /
            COST_CONSTANTS.USD_TO_INR,
        }));
        setFixedCostsApplied(true);
      }

      timerIntervalRef.current = window.setInterval(() => {
        const currentDuration = Math.floor((Date.now() - newStartTime) / 1000);
        setSessionDuration(currentDuration);

        // Calculate minutes for billing (ceiling to the next minute)
        const minutes = Math.ceil(currentDuration / 60);
        if (minutes > callDurationMinutes) {
          setCallDurationMinutes(minutes);

          // Update calling service cost and service charge
          const callingServiceCost =
            minutes * COST_CONSTANTS.CALLING_SERVICE_COST_PER_MIN;
          const serviceCharge = minutes * COST_CONSTANTS.SERVICE_CHARGE_PER_MIN;

          setCosts((prevCosts) => ({
            ...prevCosts,
            callingServiceCost,
            serviceCharge,
            totalInr:
              prevCosts.inr +
              callingServiceCost +
              serviceCharge +
              COST_CONSTANTS.FIXED_CONNECTION_COST,
            totalUsd:
              (prevCosts.inr +
                COST_CONSTANTS.FIXED_CONNECTION_COST +
                callingServiceCost +
                serviceCharge) /
              COST_CONSTANTS.USD_TO_INR,
          }));
        }
      }, 1000);
    } else if (!isConnected && timerIntervalRef.current) {
      // Stop the timer if we disconnect
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Cleanup function to clear timer when component unmounts
    return () => {
      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isConnected, fixedCostsApplied, callDurationMinutes]);

  // Wait for ICE gathering to complete (from working HTML example)
  const waitForIceGatheringComplete = async (pc: RTCPeerConnection, timeoutMs: number = 2000): Promise<void> => {
    if (pc.iceGatheringState === 'complete') return;
    console.log("Waiting for ICE gathering to complete. Current state:", pc.iceGatheringState);
    return new Promise((resolve) => {
      let timeoutId: number;
      const checkState = () => {
        console.log("icegatheringstatechange:", pc.iceGatheringState);
        if (pc.iceGatheringState === 'complete') {
          cleanup();
          resolve();
        }
      };
      const onTimeout = () => {
        console.warn(`ICE gathering timed out after ${timeoutMs} ms.`);
        cleanup();
        resolve();
      };
      const cleanup = () => {
        pc.removeEventListener('icegatheringstatechange', checkState);
        clearTimeout(timeoutId);
      };
      pc.addEventListener('icegatheringstatechange', checkState);
      timeoutId = window.setTimeout(onTimeout, timeoutMs);
      // Checking the state again to avoid any eventual race condition
      checkState();
    });
  };

  // Connect to voice agent using SmallWebRTC compatible approach
  const connectToAgent = async (
    runtimeFields?: AgentRuntimeFields,
    preparedStream?: MediaStream | null
  ) => {
    // Double check we have a microphone stream
    const activeStream = preparedStream || streamRef.current || stream;
    if (!activeStream) {
      setErrorMessage("Please enable microphone first");
      return;
    }

    setErrorMessage("");
    setIsConnecting(true);
    setProviderDebug(null);
    setProviderDebugSessionId(null);

    // Reset token usage and costs when connecting
    setSessionDuration(0);
    setCallDurationMinutes(0);
    resetCostCounter();

    try {
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "suspended"
      ) {
        await audioContextRef.current.resume();
      }

      // Create new RTCPeerConnection with reliable STUN/TURN servers
      const isProduction = window.location.hostname !== 'localhost';
      const pc = new RTCPeerConnection({
        iceServers: [
          // Google's reliable public STUN servers
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          // Fallback AWS TURN server (if accessible)
          {
            "urls": ["stun:35.200.136.192:3478", "turn:35.200.136.192:3478"],
            "username": "uktiuser",
            "credential": "uktipass"
          }
        ],
        iceCandidatePoolSize: isProduction ? 10 : 0,
        iceTransportPolicy: "all", // Allow both direct and relay connections
        bundlePolicy: "max-bundle",
        rtcpMuxPolicy: "require"
      });
      peerConnectionRef.current = pc;

      // Setup connection state handlers
      pc.onconnectionstatechange = () => {
        console.log("onconnectionstatechange", pc?.connectionState);
        if (pc.connectionState === "connected") {
          setIsConnected(true);
          setIsConnecting(false);
        } else if (
          pc.connectionState === "disconnected" ||
          pc.connectionState === "failed" ||
          pc.connectionState === "closed"
        ) {
          setIsConnected(false);
          setAudioActive(false);
          setIsConnecting(false);
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log("oniceconnectionstatechange", pc?.iceConnectionState);
        
        // Handle ICE connection failures with restart logic
        if (pc?.iceConnectionState === 'failed') {
          console.log("ICE connection failed, attempting restart...");
          // Trigger ICE restart
          pc.restartIce();
        } else if (pc?.iceConnectionState === 'disconnected') {
          console.log("ICE connection disconnected, monitoring for recovery...");
          // Set a timeout to restart if not recovered
          setTimeout(() => {
            if (pc?.iceConnectionState === 'disconnected') {
              console.log("ICE still disconnected after timeout, restarting...");
              pc.restartIce();
            }
          }, 5000);
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log("New ICE candidate:", event.candidate);
          console.log(`ICE candidate type: ${event.candidate.type}, protocol: ${event.candidate.protocol}, address: ${event.candidate.address || 'N/A'}`);
        } else {
          console.log("All ICE candidates have been sent.");
        }
      };

      // Handle incoming tracks (for audio output)
      pc.ontrack = (evt) => {
        console.log("Received remote track:", evt.track.kind);
        evt.track.onended = () => {
          console.warn("Remote audio track ended");
          setAudioActive(false);
        };
        if (audioRef.current && audioRef.current.srcObject !== evt.streams[0]) {
          audioRef.current.srcObject = evt.streams[0];
          audioRef.current.onplaying = () => {
            console.log("Remote audio playback started");
            setIsConnected(true);
            setIsConnecting(false);
            setAudioActive(true);
          };
          audioRef.current.onpause = () => {
            console.warn("Remote audio playback paused");
          };
          audioRef.current.onstalled = () => {
            console.warn("Remote audio playback stalled");
          };
          audioRef.current.onerror = () => {
            console.error("Remote audio playback error");
            setErrorMessage("Agent audio could not be played. Please reconnect.");
          };
          setOutputStream(evt.streams[0]);
          // Setup analyser for remote output to control mic gain
          setupRemoteAnalyser(evt.streams[0]);
        }
      };

      const audioTrack = activeStream.getAudioTracks()[0];
      pc.addTransceiver(audioTrack, { direction: 'sendrecv' });

      // Create data channel for text communication
      const dataChannel = pc.createDataChannel("text");
      dataChannelRef.current = dataChannel;

      // Setup data channel handlers
      dataChannel.onopen = () => {
        console.log("Data channel opened");
        setIsConnecting(false);
      };

      dataChannel.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === "disconnected") {
            console.log("Disconnecting from agent");
            disconnectFromAgent();
          }
          // Process token usage for Gemini mode
          if (message.type === "token_usage") {
            // Extract token counts from Gemini format
            const inputText = message.data.input?.text || 0;
            const inputAudio = message.data.input?.audio || 0;
            const outputText = message.data.output?.text || 0;
            const outputAudio = message.data.output?.audio || 0;

            // Update token usage
            setTokenUsage((prevUsage) => {
              // For Gemini, we need to accumulate the counts
              const newInputText =
                prevUsage.input_token_details.text + inputText;
              const newInputAudio =
                prevUsage.input_token_details.audio + inputAudio;
              const newOutputText =
                prevUsage.output_token_details.text + outputText;
              const newOutputAudio =
                prevUsage.output_token_details.audio + outputAudio;

              // Calculate total tokens
              const totalInputTokens = newInputText + newInputAudio;
              const totalOutputTokens = newOutputText + newOutputAudio;

              const newUsage = {
                input_tokens: totalInputTokens,
                output_tokens: totalOutputTokens,
                input_token_details: {
                  text: newInputText,
                  audio: newInputAudio,
                  cached: 0, // Gemini doesn't have cached tokens concept
                  cached_text: 0,
                  cached_audio: 0,
                },
                output_token_details: {
                  text: newOutputText,
                  audio: newOutputAudio,
                },
              };

              // Calculate costs in USD and INR using Gemini rates
              const textInputCost =
                (newUsage.input_token_details.text / 1000000) *
                COST_CONSTANTS.GEMINI_INPUT_TEXT;
              const audioInputCost =
                (newUsage.input_token_details.audio / 1000000) *
                COST_CONSTANTS.GEMINI_INPUT_AUDIO;
              const textOutputCost =
                (newUsage.output_token_details.text / 1000000) *
                COST_CONSTANTS.GEMINI_OUTPUT_TEXT;
              const audioOutputCost =
                (newUsage.output_token_details.audio / 1000000) *
                COST_CONSTANTS.GEMINI_OUTPUT_AUDIO;

              const totalUsdCost =
                textInputCost +
                audioInputCost +
                textOutputCost +
                audioOutputCost;
              const totalInrCost = totalUsdCost * COST_CONSTANTS.USD_TO_INR;

              // Update costs
              setCosts((prevCosts) => ({
                usd: totalUsdCost,
                inr: totalInrCost,
                fixedCost: prevCosts.fixedCost,
                callingServiceCost: prevCosts.callingServiceCost,
                serviceCharge: prevCosts.serviceCharge,
                totalInr:
                  totalInrCost +
                  prevCosts.fixedCost +
                  prevCosts.callingServiceCost +
                  prevCosts.serviceCharge,
              }));

              return newUsage;
            });
          }

          // Handle errors from the backend
          if (message.type === "error" && message.data?.message) {
            console.error("Error from backend:", message.data.message);

            // Handle file not found errors specifically
            if (
              message.data.message.includes("not found") ||
              message.data.message.includes("404") ||
              message.data.message.includes("no such file")
            ) {
              setErrorMessage(
                "Audio file not found. The recording may have been deleted or is unavailable."
              );
            } else {
              setErrorMessage(message.data.message);
            }
          }

          // Capture user transcriptions
          if (message.type === "user_transcription" && message.data?.text) {
            setChatMessages((prev) => {
              const newMessages = [
                ...prev,
                {
                  type: "user" as const,
                  text: message.data.text,
                  timestamp: Date.now(),
                },
              ];
              return newMessages;
            });
          }

          // Capture agent responses
          if (message.type === "output_transcription" && message.data?.text) {
            setChatMessages((prev) => {
              const newMessages = [
                ...prev,
                {
                  type: "agent" as const,
                  text: formatAgentTranscript(message.data.text),
                  timestamp: Date.now(),
                },
              ];
              return newMessages;
            });
          }
        } catch (e) {
          console.error("Error parsing data channel message:", e);
          console.log("Received raw message:", event.data);
        }
      };

      // Create offer and wait for ICE gathering to complete
      await pc.setLocalDescription(await pc.createOffer());
      await waitForIceGatheringComplete(pc);

      // Construct the full WebRTC endpoint URL
      const path = apiPath || `/agents/${agentUuid}`;
      const webrtcEndpoint = `${API_BASE_URL}${path}/offer`;

      const token = localStorage.getItem("token");
      
      // Generate a unique connection ID for this session to avoid reusing closed connections
      const connectionId = `pc_${agentUuid}_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      
      // Send offer to server (no individual ICE candidates needed for SmallWebRTC)
      const response = await fetch(webrtcEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sdp: pc.localDescription?.sdp,
          type: pc.localDescription?.type,
          pc_id: connectionId,
          fields: runtimeFields,
          name:
            typeof runtimeFields?.name === "string"
              ? runtimeFields.name
              : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle server response
      const serverResponse = await response.json();
      setProviderDebug(serverResponse.debug || null);
      if (typeof serverResponse.startup_message === "string") {
        const startupMessage = formatAgentTranscript(
          serverResponse.startup_message
        );
        if (startupMessage) {
          setChatMessages((previousMessages) => {
            const alreadyVisible = previousMessages.some(
              (message) =>
                message.type === "agent" && message.text === startupMessage
            );
            if (alreadyVisible) return previousMessages;
            return [
              ...previousMessages,
              {
                type: "agent" as const,
                text: startupMessage,
                timestamp: Date.now(),
              },
            ];
          });
        }
      }
      await pc.setRemoteDescription(serverResponse);

    } catch (error) {
      console.error("Error connecting:", error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : "Failed to connect to voice agent";
      setErrorMessage(errorMsg);
      setAudioActive(false);
      setIsConnecting(false);

      // Clean up failed connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
    }
  };

  const handleTryInBrowserClick = () => {
    if (!isAdmin && remainingUses !== null && remainingUses <= 0) return;
    setErrorMessage("");
    setShowBrowserTryModal(true);
  };

  const handleBrowserTryConnect = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isBrowserTryStarting || isConnecting) return;

    setIsBrowserTryStarting(true);
    setErrorMessage("");

    try {
      const preparedStream = await setupMicrophone();
      if (!preparedStream) return;

      const runtimePayload = buildRuntimeFieldsPayload(
        browserDirectFields,
        browserExtraInfos,
        {
          orderedKeys: browserDirectFieldKeys,
          fallbackDefaults: directFieldDefaults,
        }
      );
      setShowBrowserTryModal(false);
      await connectToAgent(runtimePayload, preparedStream);
    } finally {
      setIsBrowserTryStarting(false);
    }
  };

  const handleBrowserTryModalClose = () => {
    if (isBrowserTryStarting || isConnecting) return;
    setShowBrowserTryModal(false);
    if (!isConnected) releaseMicrophone();
  };

  // Toggle mute
  const toggleMute = () => {
    const activeStream = streamRef.current || stream;
    if (activeStream) {
      activeStream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  // Helper function to format duration in HH:MM:SS
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const pad = (num: number) => num.toString().padStart(2, "0");

    if (hours > 0) {
      return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
    } else {
      return `${pad(minutes)}:${pad(remainingSeconds)}`;
    }
  };

  const formatProviderLabel = (provider?: string | null): string => {
    if (!provider) return "unknown";
    return provider
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const formatStageLabel = (stage: string): string => stage.toUpperCase();

  const formatProbeLatency = (totalMs?: number | null): string => {
    if (typeof totalMs !== "number") return "no latency";
    if (totalMs >= 1000) return `${(totalMs / 1000).toFixed(2)}s`;
    return `${Math.round(totalMs)}ms`;
  };

  const formatProbeLatencySeconds = (totalMs?: number | null): string => {
    if (typeof totalMs !== "number") return "no latency";
    return `${(totalMs / 1000).toFixed(2)}s`;
  };

  const getProviderLatencyReason = (
    stage: string,
    comparison:
      | NonNullable<DirectCallProviderCheck["comparisons"]>[string]
      | undefined,
    currentProvider?: string | null,
    selectedProvider?: string | null
  ): string | null => {
    if (
      !comparison?.results ||
      !currentProvider ||
      !selectedProvider ||
      currentProvider === selectedProvider
    ) {
      return null;
    }

    const currentResult = comparison.results.find(
      (result) => result.provider === currentProvider
    );
    const selectedResult = comparison.results.find(
      (result) => result.provider === selectedProvider
    );

    if (
      !currentResult?.success ||
      !selectedResult?.success ||
      typeof currentResult.total_ms !== "number" ||
      typeof selectedResult.total_ms !== "number"
    ) {
      return null;
    }

    const thresholdMs = currentResult.total_ms * 0.85;
    if (selectedResult.total_ms > thresholdMs) {
      return null;
    }

    return `${formatStageLabel(stage)} uses ${formatProviderLabel(
      selectedProvider
    )} for this call because it responded in ${formatProbeLatencySeconds(
      selectedResult.total_ms
    )}, at least 15% faster than ${formatProviderLabel(
      currentProvider
    )} at ${formatProbeLatencySeconds(currentResult.total_ms)}.`;
  };

  const getProviderCheckStages = (
    check: DirectCallProviderCheck | null
  ): string[] => {
    if (!check) return [];
    const stages = new Set<string>();
    Object.keys(check.providers || {}).forEach((stage) => stages.add(stage));
    Object.keys(check.selected_providers || {}).forEach((stage) =>
      stages.add(stage)
    );
    Object.keys(check.comparisons || {}).forEach((stage) => stages.add(stage));
    return ["stt", "llm", "tts"].filter((stage) => stages.has(stage));
  };

  const getTraceStageLabel = (stage?: string): string => {
    if (!stage) return "Event";
    if (stage === "vad") return "Caller speech";
    if (stage === "stt") return "STT";
    if (stage === "llm") return "LLM";
    if (stage === "tts") return "Bot audio";
    if (stage === "transcript_to_llm") return "Transcript -> LLM";
    if (stage === "full_turn") return "Full turn";
    return stage.toUpperCase();
  };

  const getTraceEventLabel = (event: DirectCallTraceEvent): string => {
    if (event.type === "tool_call") return event.tool_name || "Tool called";
    if (event.type === "token_usage") return "Prompt cache";
    if (event.type === "vad_activity") return getTraceStageLabel("vad");
    return getTraceStageLabel(event.stage);
  };

  const formatTraceEventValue = (event: DirectCallTraceEvent): string => {
    if (event.type === "tool_call") return "called";
    if (event.type === "token_usage") {
      const state = event.promptCacheState || "unknown";
      return typeof event.cacheReadInputTokens === "number"
        ? `${state} ${event.cacheReadInputTokens}`
        : state;
    }
    return formatDirectCallTraceDuration(event.duration_ms);
  };

  const formatTraceTimeRange = (event?: DirectCallTraceEvent): string => {
    if (!event?.started_at && !event?.completed_at) return "-";
    const start = formatDirectCallTraceTimestamp(event.started_at);
    const end = formatDirectCallTraceTimestamp(event.completed_at);
    if (start === "-") return end;
    if (end === "-") return start;
    return `${start} - ${end}`;
  };

  const formatToolVariables = (event: DirectCallTraceEvent): string[] => {
    if (!event.variables) return [];
    return Object.entries(event.variables).map(
      ([key, value]) => `${key}=${value ?? ""}`
    );
  };

  const getTraceSeverityClass = (stage?: string, durationMs?: number): string => {
    const severity = getDirectCallTraceSeverity(stage, durationMs);
    if (severity === "critical") {
      return "border-red-500/50 bg-red-500/10 text-red-500";
    }
    if (severity === "warning") {
      return "border-amber-500/50 bg-amber-500/10 text-amber-500";
    }
    return "border-[var(--border)] bg-[var(--background)] text-[var(--foreground)]";
  };

  const traceTurns = groupDirectCallTraceEvents(providerTraceEvents);
  const traceSummary = summarizeDirectCallTrace(providerTraceEvents);

  const buildTraceExportPayload = () => ({
    exported_at: new Date().toISOString(),
    provider_debug_session_id: providerDebugSessionId,
    provider_debug: providerDebug,
    summary: traceSummary,
    turns: traceTurns.map((turn) => {
      const summary = summarizeDirectCallTraceTurn(turn);
      return {
        turn_number: turn.turnNumber,
        started_at:
          summary.vad?.started_at ||
          summary.stt?.started_at ||
          turn.events[0]?.started_at ||
          null,
        completed_at:
          summary.fullTurn?.completed_at ||
          turn.events[turn.events.length - 1]?.completed_at ||
          null,
        caller_speech_ms: summary.vad?.duration_ms ?? null,
        stt_ms: summary.stt?.duration_ms ?? null,
        transcript_to_llm_ms: summary.transcriptToLlm?.duration_ms ?? null,
        llm_first_text_ms: summary.llm?.duration_ms ?? null,
        tts_first_audio_ms: summary.tts?.duration_ms ?? null,
        full_turn_ms: summary.fullTurn?.duration_ms ?? null,
        prompt_cache_state: summary.tokenUsage?.promptCacheState ?? null,
        cache_read_input_tokens: summary.tokenUsage?.cacheReadInputTokens ?? null,
        token_usage_processor: summary.tokenUsage?.processor ?? null,
        tool_calls: summary.toolCalls.map((event) => ({
          tool_name: event.tool_name,
          variables: event.variables,
        })),
        events: turn.events,
      };
    }),
    raw_events: providerTraceEvents,
  });

  const exportDirectCallTrace = () => {
    const payload = buildTraceExportPayload();
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `direct-call-trace-${providerDebugSessionId || "latest"}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const DirectCallTracePanel = () => (
    <div className="w-full rounded-lg border border-[var(--border)] bg-[var(--card)] shadow-md overflow-hidden">
      <div className="border-b border-[var(--border)] p-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-[var(--foreground)] flex items-center gap-2">
                <FaHistory className="h-4 w-4 text-[var(--secondary)]" />
                Live Turn Trace
              </h3>
              <p className="mt-1 text-xs text-[var(--foreground-muted)]">
                Readable per-turn diagnostics for speech, STT, LLM cache, and bot audio.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={exportDirectCallTrace}
                disabled={providerTraceEvents.length === 0}
                className="rounded-md border border-[var(--border)] bg-[var(--background)] px-2.5 py-1 text-xs font-medium text-[var(--foreground-muted)] hover:text-[var(--foreground)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Export
              </button>
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium border ${
                  providerDebugSessionId
                    ? "border-[var(--color-accent)]/50 text-[var(--color-accent)] bg-[var(--color-accent)]/10"
                    : "border-[var(--border)] text-[var(--foreground-muted)]"
                }`}
              >
                {providerDebugSessionId ? "Live" : "Idle"}
              </span>
            </div>
          </div>

          {providerDebug?.providers && (
            <div className="grid grid-cols-3 gap-2">
              {["stt", "llm", "tts"].map((stage) => (
                <div
                  key={stage}
                  className="rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-2"
                >
                  <div className="text-[10px] uppercase text-[var(--foreground-muted)]">
                    {stage}
                  </div>
                  <div className="truncate text-xs font-semibold text-[var(--foreground)]">
                    {formatProviderLabel(providerDebug.providers?.[stage])}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      <div className="grid grid-cols-3 border-b border-[var(--border)] text-xs">
        <div className="p-3">
          <div className="text-[var(--foreground-muted)]">Turns</div>
          <div className="text-lg font-semibold text-[var(--foreground)]">
            {traceSummary.turnCount}
          </div>
        </div>
        <div className="border-l border-[var(--border)] p-3">
          <div className="text-[var(--foreground-muted)]">Events</div>
          <div className="text-lg font-semibold text-[var(--foreground)]">
            {traceSummary.totalEvents}
          </div>
        </div>
        <div className="border-l border-[var(--border)] p-3">
          <div className="text-[var(--foreground-muted)]">Slowest</div>
          <div
            className={`truncate text-sm font-semibold ${
              traceSummary.slowestStage?.severity === "critical"
                ? "text-red-500"
                : traceSummary.slowestStage?.severity === "warning"
                ? "text-amber-500"
                : "text-[var(--foreground)]"
            }`}
          >
            {traceSummary.slowestStage
              ? `${getTraceStageLabel(traceSummary.slowestStage.stage)} ${formatDirectCallTraceDuration(
                  traceSummary.slowestStage.duration_ms
                )}`
              : "-"}
          </div>
        </div>
      </div>

      <div className="max-h-[460px] overscroll-contain overflow-y-auto p-3">
        {traceTurns.length === 0 ? (
          <div className="rounded-md border border-dashed border-[var(--border)] p-5 text-center">
            <div className="text-sm font-medium text-[var(--foreground)]">
              Waiting for turn timings
            </div>
            <div className="mt-1 text-xs text-[var(--foreground-muted)]">
              The first provider event appears after the caller speaks or the bot emits audio.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {traceTurns.map((turn) => {
              const isExpanded = expandedTraceTurn === turn.turnNumber;
              const turnSummary = summarizeDirectCallTraceTurn(turn);
              const startedAt =
                turnSummary.vad?.started_at ||
                turnSummary.stt?.started_at ||
                turn.events[0]?.started_at ||
                null;
              const completedAt =
                turnSummary.fullTurn?.completed_at ||
                turn.events[turn.events.length - 1]?.completed_at ||
                null;
              const diagnosisItems = [
                {
                  key: "caller",
                  label: "Caller speech",
                  value: formatDirectCallTraceDuration(turnSummary.vad?.duration_ms),
                  detail: turnSummary.vad
                    ? `Detected by VAD, ${formatTraceTimeRange(turnSummary.vad)}`
                    : "Waiting for VAD start/stop.",
                  event: turnSummary.vad,
                },
                {
                  key: "stt",
                  label: "STT",
                  value: formatDirectCallTraceDuration(turnSummary.stt?.duration_ms),
                  detail: turnSummary.stt
                    ? `Transcript after caller stopped, ${formatTraceTimeRange(turnSummary.stt)}`
                    : "Waiting for transcript timing.",
                  event: turnSummary.stt,
                },
                {
                  key: "llm",
                  label: "LLM + cache",
                  value: turnSummary.tokenUsage
                    ? `${turnSummary.tokenUsage.promptCacheState || "unknown"} ${
                        turnSummary.tokenUsage.cacheReadInputTokens ?? "-"
                      }`
                    : formatDirectCallTraceDuration(turnSummary.llm?.duration_ms),
                  detail: turnSummary.tokenUsage
                    ? `${turnSummary.tokenUsage.processor || "LLM"} used ${
                        turnSummary.tokenUsage.promptTokens ?? "-"
                      } prompt tokens; cache read ${
                        turnSummary.tokenUsage.cacheReadInputTokens ?? "-"
                      }.`
                    : "Waiting for LLM token usage.",
                  event: turnSummary.llm || turnSummary.tokenUsage,
                },
                {
                  key: "audio",
                  label: "Bot audio",
                  value: formatDirectCallTraceDuration(turnSummary.tts?.duration_ms),
                  detail: turnSummary.tts
                    ? `First audio from ${formatProviderLabel(
                        turnSummary.tts.provider
                      )}, ${formatTraceTimeRange(turnSummary.tts)}`
                    : "Waiting for first audio timing.",
                  event: turnSummary.tts,
                },
              ];
              const slowEvent = turn.events.find(
                (event) =>
                  getDirectCallTraceSeverity(event.stage, event.duration_ms) !==
                  "normal"
              );
              return (
                <div
                  key={turn.turnNumber}
                  className="rounded-md border border-[var(--border)] bg-[var(--background)]"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedTraceTurn(isExpanded ? null : turn.turnNumber)
                    }
                    className="flex w-full items-center justify-between gap-3 p-3 text-left"
                  >
                    <div>
                      <div className="text-sm font-semibold text-[var(--foreground)]">
                        Turn {turn.turnNumber}
                      </div>
                      <div className="mt-1 text-xs text-[var(--foreground-muted)]">
                        {formatDirectCallTraceTimestamp(startedAt)} - {formatDirectCallTraceTimestamp(completedAt)}
                        {slowEvent
                          ? ` • slow ${getTraceStageLabel(slowEvent.stage)}`
                          : ""}
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-1.5 text-right">
                      {diagnosisItems.map((item) => (
                        <span
                          key={item.key}
                          className={`rounded border px-2 py-1 text-[11px] font-semibold ${getTraceSeverityClass(
                            item.event?.stage,
                            item.event?.duration_ms
                          )}`}
                        >
                          {item.label} <span className="font-normal">{item.value}</span>
                        </span>
                      ))}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-[var(--border)] p-3 space-y-2">
                      <div className="grid gap-2 md:grid-cols-2">
                        {diagnosisItems.map((item) => (
                          <div
                            key={item.key}
                            className={`rounded-md border px-3 py-2 text-xs ${getTraceSeverityClass(
                              item.event?.stage,
                              item.event?.duration_ms
                            )}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[var(--foreground-muted)]">
                                {item.label}
                              </span>
                              <span className="font-semibold">{item.value}</span>
                            </div>
                            <div className="mt-1 text-[11px] opacity-80">
                              {item.detail}
                            </div>
                          </div>
                        ))}
                      </div>

                      {turnSummary.toolCalls.length > 0 && (
                        <div className="rounded-md border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-xs">
                          <div className="font-medium text-[var(--foreground)]">Tools</div>
                          <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-[var(--foreground-muted)]">
                            {turnSummary.toolCalls.map((event) => (
                              <span key={event.sequence}>
                                {event.tool_name}: {formatToolVariables(event).join(", ")}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <details className="rounded-md border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-xs">
                        <summary className="cursor-pointer font-medium text-[var(--foreground-muted)]">
                          Raw events ({turn.events.length})
                        </summary>
                        <div className="mt-2 space-y-2">
                          {turn.events.map((event) => (
                        <div
                          key={event.sequence}
                          className={`rounded-md border px-3 py-2 text-xs ${getTraceSeverityClass(
                            event.stage,
                            event.duration_ms
                          )}`}
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-medium">
                              {getTraceEventLabel(event)}
                            </span>
                            <span>
                              {formatTraceEventValue(event)}
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] opacity-80">
                            <span>{formatProviderLabel(event.provider)}</span>
                            <span>{formatTraceTimeRange(event)}</span>
                            {event.processor && <span>{event.processor}</span>}
                            {event.from_event && event.to_event && (
                              <span>
                                {event.from_event} -&gt; {event.to_event}
                              </span>
                            )}
                            {event.type === "token_usage" && (
                              <span>
                                prompt={event.promptTokens ?? "-"} cached={event.cacheReadInputTokens ?? "-"}
                              </span>
                            )}
                          </div>
                        </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // Reset token usage and costs
  const resetCostCounter = () => {
    setTokenUsage({
      input_tokens: 0,
      output_tokens: 0,
      input_token_details: {
        text: 0,
        audio: 0,
        cached: 0,
        cached_text: 0,
        cached_audio: 0,
      },
      output_token_details: { text: 0, audio: 0 },
    });
    setCosts({
      usd: 0,
      inr: 0,
      fixedCost: 0,
      callingServiceCost: 0,
      serviceCharge: 0,
      totalInr: 0,
    });
    setFixedCostsApplied(false);
    setCallDurationMinutes(0);
  };

  // Add this function to track if a session was meaningful
  const wasSessionMeaningful = () => {
    // Check if session duration was meaningful (more than 5 seconds)
    if (sessionDuration > 5) return true;

    // Or if there were any chat messages exchanged
    if (chatMessages.length > 0) return true;

    return false;
  };

  // Disconnect from voice agent
  const disconnectFromAgent = async () => {
    try {
      setErrorMessage(null);

      // Check if the session was meaningful before we change any state
      const shouldDeductUse = wasSessionMeaningful();

      // First set UI state to prevent user interaction during disconnect
      setIsConnected(false);
      setAudioActive(false);
      setOutputStream(null);
      setIsConnecting(false);

      // Stop the session timer
      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      if (!agentUuid) {
        setErrorMessage("No active agent found");
        return;
      }

      // Decrement remaining uses counter after successful call
      if (remainingUses !== null && !isAdmin && shouldDeductUse) {
        const newRemainingUses = Math.max(0, remainingUses - 1);
        setRemainingUses(newRemainingUses);

        // Update localStorage
        localStorage.setItem("remainingUses", newRemainingUses.toString());

        // Update the global counter if available
        if (window.updateRemainingUses) {
          window.updateRemainingUses(newRemainingUses);
        }
      }

      // Cancel remote level monitoring if running
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      releaseMicrophone();

      // Close the peer connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      // Close data channel
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
        dataChannelRef.current = null;
      }

      // Clean up audio element
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.srcObject = null;
        } catch (e) {
          console.error("Error cleaning up audio element:", e);
        }
      }
    } catch (error) {
      console.error("Error disconnecting:", error);
      setErrorMessage("Error during disconnection. Please refresh the page.");
    }
  };

  // Toggle cost breakdown modal
  const toggleCostModal = () => {
    setShowCostModal(!showCostModal);
  };

  // Calculate detailed costs for display
  const calculateDetailedCosts = () => {
    if (!tokenUsage.input_token_details || !tokenUsage.output_token_details) {
      return {
        textInputCost: 0,
        audioInputCost: 0,
        textOutputCost: 0,
        audioOutputCost: 0,
        totalUsdCost: 0,
        totalInrCost: 0,
        perMinuteUsdCost: 0,
        perMinuteInrCost: 0,
        fixedCost: costs.fixedCost,
        callingServiceCost: costs.callingServiceCost,
        serviceCharge: costs.serviceCharge,
        grandTotalInr: costs.totalInr,
        grandTotalUsd: costs.totalInr / COST_CONSTANTS.USD_TO_INR,
      };
    }

    const textInputCost =
      (tokenUsage.input_token_details.text / 1000000) *
      COST_CONSTANTS.GEMINI_INPUT_TEXT;
    const audioInputCost =
      (tokenUsage.input_token_details.audio / 1000000) *
      COST_CONSTANTS.GEMINI_INPUT_AUDIO;
    const textOutputCost =
      (tokenUsage.output_token_details.text / 1000000) *
      COST_CONSTANTS.GEMINI_OUTPUT_TEXT;
    const audioOutputCost =
      (tokenUsage.output_token_details.audio / 1000000) *
      COST_CONSTANTS.GEMINI_OUTPUT_AUDIO;

    const totalUsdCost =
      textInputCost + audioInputCost + textOutputCost + audioOutputCost;
    const totalInrCost = totalUsdCost * COST_CONSTANTS.USD_TO_INR;

    // Calculate the grand total including fixed and service costs
    const grandTotalInr =
      totalInrCost +
      costs.fixedCost +
      costs.callingServiceCost +
      costs.serviceCharge;

    const grandTotalUsd = grandTotalInr / COST_CONSTANTS.USD_TO_INR;

    // Calculate per-minute costs
    // Use a minimum of 1 minute to avoid division by zero or unrealistic per-minute costs
    const minutes = Math.max(sessionDuration / 60, 1);
    const perMinuteUsdCost = grandTotalUsd / minutes;
    const perMinuteInrCost = grandTotalInr / minutes;

    return {
      textInputCost,
      audioInputCost,
      textOutputCost,
      audioOutputCost,
      totalUsdCost,
      totalInrCost,
      perMinuteUsdCost,
      perMinuteInrCost,
      fixedCost: costs.fixedCost,
      callingServiceCost: costs.callingServiceCost,
      serviceCharge: costs.serviceCharge,
      grandTotalInr,
      grandTotalUsd,
    };
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      try {
        // Clean up audio element
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.srcObject = null;
        }

        releaseMicrophone();

        // Close the peer connection
        if (peerConnectionRef.current) {
          peerConnectionRef.current.close();
        }

        // Close data channel
        if (dataChannelRef.current) {
          dataChannelRef.current.close();
        }

        // Cancel any active animation frame for remote monitoring
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        // Clean up timer interval
        if (timerIntervalRef.current) {
          window.clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      } catch (e) {
        console.error("Error during cleanup:", e);
      }
    };
  }, [releaseMicrophone]);

  // Handle back to selection
  const handleBack = () => {
    if (onBackToSelection) {
      onBackToSelection();
    } else {
      navigate("/");
    }
  };

  // Format timestamp for chat messages
  const formatMessageTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Handle touch events for swipe controls
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY === null) return;

    const touchY = e.touches[0].clientY;
    const diff = touchStartY - touchY;

    // If swiping up and chat is minimized, expand it
    if (diff > 50 && !chatExpanded) {
      setChatExpanded(true);
    }
    // If swiping down and chat is expanded, minimize it
    else if (diff < -50 && chatExpanded) {
      setChatExpanded(false);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartY(null);
  };

  // Handle phone number input changes
  const handlePhoneNumberChange = (value: string | undefined) => {
    setPhoneNumber(value || "");
    resetProviderCheck();
  };

  const getPhoneCallCooldownKey = (formattedPhone: string): string =>
    `${agentUuid}:${formattedPhone}`;

  const getPhoneCallCooldownRemainingSeconds = (
    formattedPhone: string
  ): number => {
    const key = getPhoneCallCooldownKey(formattedPhone);
    const cooldownUntil = phoneCallCooldownsRef.current[key] || 0;
    return Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
  };

  const reservePhoneCallCooldown = (formattedPhone: string): void => {
    const key = getPhoneCallCooldownKey(formattedPhone);
    const cooldownUntil = Date.now() + PHONE_CALL_CLIENT_COOLDOWN_MS;
    phoneCallCooldownsRef.current[key] = cooldownUntil;
    setPhoneCallCooldownUntil(cooldownUntil);
  };

  const releasePhoneCallCooldown = (formattedPhone: string): void => {
    const key = getPhoneCallCooldownKey(formattedPhone);
    delete phoneCallCooldownsRef.current[key];
    setPhoneCallCooldownUntil(0);
  };

  // Validate phone number
  const validatePhoneNumber = (): boolean => {
    setPhoneValidationMsg(null);

    if (!phoneNumber.trim()) {
      setPhoneValidationMsg("Phone number is required");
      return false;
    }

    if (!validateIndianPhone(phoneNumber)) {
      setPhoneValidationMsg("Please enter a valid Indian phone number");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!phoneCallCooldownUntil) return;

    const remainingMs = phoneCallCooldownUntil - Date.now();
    if (remainingMs <= 0) {
      setPhoneCallCooldownUntil(0);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setPhoneCallCooldownUntil(0);
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [phoneCallCooldownUntil]);

  // Handle phone call submission
  const handlePhoneCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhoneNumber()) {
      return;
    }

    const formattedPhone = formatIndianPhone(phoneNumber);
    const cooldownRemainingSeconds =
      getPhoneCallCooldownRemainingSeconds(formattedPhone);
    if (cooldownRemainingSeconds > 0) {
      setPhoneCallMessage({
        type: "error",
        title: "Call Already Started",
        text: `Please wait ${cooldownRemainingSeconds}s before placing another call to ${formattedPhone}.`,
      });
      return;
    }

    setIsPhoneCallProcessing(true);
    setPhoneCallMessage(null);
    setProviderDebug(null);
    setProviderDebugSessionId(null);
    setProviderTraceEvents([]);
    setExpandedTraceTurn(null);
    reservePhoneCallCooldown(formattedPhone);

    try {
      const runtimePayload = buildRuntimeFieldsPayload(
        phoneCallDirectFields,
        phoneCallExtraInfos,
        {
          orderedKeys: phoneDirectFieldKeys,
          fallbackDefaults: directFieldDefaults,
        }
      );
      const providerCheckKey = buildProviderCheckRequestKey(
        formattedPhone,
        runtimePayload
      );

      if (isAdmin) {
        latestProviderCheckRequestKeyRef.current = providerCheckKey;
        setProviderCheck(null);

        void checkPhoneCallProviders(
          formattedPhone,
          agentUuid,
          runtimePayload,
          typeof runtimePayload.name === "string"
            ? runtimePayload.name
            : undefined
        ).then((checkResult) => {
          if (latestProviderCheckRequestKeyRef.current !== providerCheckKey) {
            return;
          }

          if (checkResult.success && checkResult.provider_check) {
            setProviderCheck(checkResult.provider_check);
          }
        });
      }

      const result = await initiatePhoneCall(
        formattedPhone,
        agentUuid,
        runtimePayload,
        typeof runtimePayload.name === "string"
          ? runtimePayload.name
          : undefined
      );

      if (result.success) {
        setProviderDebug(result.provider_debug || null);
        setProviderDebugSessionId(result.provider_debug_session_id || null);
        const payloadDirectFields = Object.entries(runtimePayload).reduce(
          (acc, [key, value]) => {
            if (key === USER_DETAILS_KEY) return acc;
            acc[key] = value == null ? "" : String(value);
            return acc;
          },
          {} as Record<string, string>
        );
        const payloadExtraInfosRaw = runtimePayload[USER_DETAILS_KEY];
        const payloadExtraInfos =
          payloadExtraInfosRaw &&
          typeof payloadExtraInfosRaw === "object" &&
          !Array.isArray(payloadExtraInfosRaw)
            ? Object.entries(payloadExtraInfosRaw as Record<string, unknown>).map(
                ([key, value], index) => ({
                  id: `initiated-${index}-${key}`,
                  key,
                  value: value == null ? "" : String(value),
                })
              )
            : [];

        setPreviousCallValues({
          phoneNumber: formattedPhone,
          directFields: payloadDirectFields,
          extraInfos: payloadExtraInfos.map((entry) => ({ ...entry })),
        });
        setPhoneCallMessage({
          type: "success",
          text: `Call made to ${formattedPhone}`,
        });
        // Clear phone number after successful submission
        setPhoneNumber("");
        const defaults = applyPhoneRuntimeDefaults(getDefaultRuntimeFieldState(agent));
        setDirectFieldOrder(defaults.directFieldOrder);
        setDirectFieldDefaults(defaults.directFieldDefaults);
        setPhoneCallDirectFields(defaults.directFields);
        setPhoneCallExtraInfos(defaults.extraInfos);

        // Decrement remaining uses counter after successful phone call
        if (remainingUses !== null && !isAdmin) {
          const newRemainingUses = Math.max(0, remainingUses - 1);
          setRemainingUses(newRemainingUses);

          // Update localStorage
          localStorage.setItem("remainingUses", newRemainingUses.toString());

          // Update the global counter if available
          if (window.updateRemainingUses) {
            window.updateRemainingUses(newRemainingUses);
          }
        }
      } else {
        releasePhoneCallCooldown(formattedPhone);
        setPhoneCallMessage({
          type: "error",
          text:
            result.message ||
            "Failed to initiate phone call. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error initiating phone call:", error);
      releasePhoneCallCooldown(formattedPhone);
      setPhoneCallMessage({
        type: "error",
        text: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsPhoneCallProcessing(false);
    }
  };

  // Toggle phone call mode
  const togglePhoneCallMode = () => {
    setPhoneCallMode(!phoneCallMode);
    const defaults = applyPhoneRuntimeDefaults(getDefaultRuntimeFieldState(agent));
    setPhoneNumber("");
    setDirectFieldOrder(defaults.directFieldOrder);
    setDirectFieldDefaults(defaults.directFieldDefaults);
    setPhoneCallDirectFields(defaults.directFields);
    setPhoneCallExtraInfos(defaults.extraInfos);
    setPhoneValidationMsg(null);
    setPhoneCallMessage(null);
    resetProviderCheck();
  };

  // Handle sharing agent link
  const handleShareAgent = () => {
    // Include access token in the link if available
    let link = `${window.location.origin}/access?agent=${agentUuid}`;

    // Debug log
    console.log("Access token for sharing:", agentAccessToken);

    // Add the access_token to the URL if available
    if (agentAccessToken) {
      link += `&token=${agentAccessToken}`;
      console.log("Final share link:", link);
    } else {
      console.warn("No access token available for sharing");
    }

    navigator.clipboard
      .writeText(link)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
      });
  };

  const phoneCallCooldownRemainingSeconds =
    phoneCallCooldownUntil > Date.now()
      ? Math.max(1, Math.ceil((phoneCallCooldownUntil - Date.now()) / 1000))
      : 0;
  const isPhoneCallCoolingDown = phoneCallCooldownRemainingSeconds > 0;

  if (loadingAgent) {
    return (
      <div className="min-h-screen bg-[var(--background)] relative">
        <div className="relative z-10 py-12 px-4">
          <div className="max-w-7xl mx-auto flex justify-center items-center py-12">
            <div className="flex flex-col items-center">
              <div className="animate-pulse">
                <HiOutlineChat className="w-16 h-16 text-[var(--color-secondary)]" />
              </div>
              <p className="mt-4 text-[var(--foreground)]">
                Loading agent details...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] relative">
      {/* Main Content */}
      <div className="relative z-10 py-1 px-3 md:py-4 md:px-4 !pt-0">
        <div className="container mx-auto max-w-7xl">
          {agent && (
            <div className="text-center mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-[var(--foreground)] mb-4 md:mb-6 text-center animate-fade-in">
                {agent.name}
              </h1>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
            {/* Main audio agent panel */}
            <div className="bg-[var(--card-container)] font-semibold border border-[var(--border)] rounded-3xl p-4 md:p-8 shadow-lg flex-1 panel-transition animate-fade-in">
              {/* Header with back button */}
              <div className="flex justify-between items-center mb-4 md:mb-6 flex-wrap gap-2">
                {phoneCallMode ? (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={togglePhoneCallMode}
                    className="w-10 py-2 px-2 text-[var(--foreground)] hover:text-[var(--foreground)]/80 flex items-center justify-center border border-[var(--border)] rounded-full group overflow-hidden hover:w-auto transition-all duration-300"
                  >
                    <IconWrapper icon={IoArrowBack} className="flex-shrink-0" />
                    <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-in-out text-sm md:text-base whitespace-nowrap">
                      Back to Web Call
                    </span>
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleBack}
                    className="w-10 py-2 px-2 text-[var(--foreground)] hover:text-[var(--foreground)]/80 flex items-center justify-center border border-[var(--border)] rounded-full group overflow-hidden hover:w-auto transition-all duration-300"
                  >
                    <IconWrapper icon={IoArrowBack} className="flex-shrink-0" />
                    <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-in-out text-sm md:text-base whitespace-nowrap">
                      Back to Agents
                    </span>
                  </motion.button>
                )}

                {!phoneCallMode && (
                  <div className="flex items-center gap-2 md:gap-4 flex-nowrap justify-end">
                    {/* Share button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleShareAgent}
                      className="bg-[var(--card)] px-3 py-1.5 font-semibold rounded-full text-sm font-medium hover:bg-[var(--card)]/80 transition-colors flex items-center gap-2 border border-[var(--border)] shadow-sm whitespace-nowrap"
                    >
                      {linkCopied ? (
                        <>
                          <HiCheck className="w-4 h-4 text-green-500" />
                          <span className="text-green-500">Copied!</span>
                        </>
                      ) : (
                        <>
                          <HiOutlineShare className="w-4 h-4" />
                          <span>Share Link</span>
                        </>
                      )}
                    </motion.button>

                    {/* Session Timer Display */}
                    <TimerDisplay />

                    <button
                      onClick={toggleCostModal}
                      className="bg-[var(--card)] px-3 py-1.5 font-semibold rounded-full text-sm font-medium hover:bg-[var(--card)]/80 transition-colors flex items-center gap-2 border border-[var(--border)] shadow-sm whitespace-nowrap"
                    >
                      <span className="text-[var(--foreground)]">
                        ${calculateDetailedCosts().grandTotalUsd.toFixed(2)} / ₹
                        {calculateDetailedCosts().grandTotalInr.toFixed(2)}
                      </span>
                    </button>
                  </div>
                )}
              </div>

              {/* Main content area */}
              <div className="flex flex-col items-center">
                {!phoneCallMode ? (
                  <>
                    {/* Standard Audio Agent UI */}
                    {/* Visualizer */}
                    <div
                      className={`w-full max-w-md aspect-square mx-auto mb-4 md:mb-8 relative ${
                        audioActive && isConnected ? "active-visualizer" : ""
                      }`}
                    >
                      <div className={`absolute inset-0 rounded-full`}></div>
                      <VoiceVisualizer
                        audioStream={outputStream}
                        isActive={audioActive && isConnected}
                      />
                    </div>
                    {/* Error message */}
                    {errorMessage && (
                      <div className="bg-red-500/10 text-red-400 px-4 py-2 rounded-lg text-sm w-full max-w-md text-center mb-4">
                        {errorMessage}
                      </div>
                    )}
                    {/* No uses remaining message */}
                    {!isAdmin &&
                      remainingUses !== null &&
                      remainingUses <= 0 && (
                        <div className="bg-amber-500/10 text-amber-500 px-4 py-3 rounded-lg text-sm w-full max-w-md text-center mb-4 border border-amber-500/20 animate-pulse-slow">
                          <p className="font-medium mb-1">
                            You have no uses remaining
                          </p>
                          <p>
                            Please contact{" "}
                            <a
                              href="mailto:contact@ukti.ai"
                              className="underline font-medium"
                            >
                              contact@ukti.ai
                            </a>{" "}
                            to request more.
                          </p>
                        </div>
                      )}
                    {/* Mic button */}
                    <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8">
                      {!isConnected ? (
                        <button
                          className={`bg-[var(--secondary)] hover:bg-[var(--secondary-hover)] text-[var(--secondary-foreground)] px-4 md:px-6 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base button-transition ${
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          onClick={handleTryInBrowserClick}
                          disabled={
                            isConnecting ||
                            isBrowserTryStarting ||
                            (!isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0)
                          }
                          hidden={
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                          }
                        >
                          <IconWrapper icon={BsMicFill} />
                          {isConnecting || isBrowserTryStarting
                            ? "Connecting..."
                            : "Try in Browser"}
                        </button>
                      ) : (
                        <>
                          <button
                            className="bg-red-500 hover:bg-red-600 text-white px-4 md:px-6 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base button-transition"
                            onClick={disconnectFromAgent}
                          >
                            Disconnect
                          </button>
                          <button
                            className={`border ${
                              isMuted
                                ? "bg-[var(--secondary)] text-[var(--secondary-foreground)]"
                                : "bg-[var(--muted)] text-[var(--muted-foreground)]"
                            } px-4 md:px-6 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base button-transition ${
                              !isMuted && isConnected
                                ? "animate-pulse-slow"
                                : ""
                            }`}
                            onClick={toggleMute}
                          >
                            {isMuted ? (
                              <>
                                <IconWrapper icon={BsMicFill} /> Unmute
                              </>
                            ) : (
                              <>
                                <IconWrapper icon={BsMicMuteFill} /> Mute
                              </>
                            )}
                          </button>
                        </>
                      )}

                      {/* Phone Call button - only show if agent has phone call capability and mic is not enabled */}
                      {agent?.phone_supported && !stream && isAdmin && (
                        <motion.button
                          whileHover={{
                            scale:
                              !isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0
                                ? 1
                                : 1.02,
                          }}
                          whileTap={{
                            scale:
                              !isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0
                                ? 1
                                : 0.98,
                          }}
                          onClick={togglePhoneCallMode}
                          disabled={
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                          }
                          hidden={
                            !isAdmin ||
                            (remainingUses !== null && remainingUses <= 0)
                          }
                          className={`bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--secondary-hover)] px-4 md:px-6 py-2.5 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base font-medium ${
                            !isAdmin ||
                            (remainingUses !== null && remainingUses <= 0)
                              ? "opacity-50 cursor-not-allowed hidden"
                              : ""
                          }`}
                        >
                          <HiPhone className="w-5 h-5" />
                          <span>Call Agent</span>
                        </motion.button>
                      )}
                    </div>

                    {/* Info panels */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 w-full">
                      {/* Limitations panel */}
                      <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-3 md:p-4 panel-transition hover-scale">
                        <h3 className="text-[var(--foreground)] font-medium text-base md:text-lg mb-2 md:mb-3">
                          Current limitations
                        </h3>
                        {agent &&
                        agent.limitations &&
                        agent.limitations.length > 0 ? (
                          <ul className="space-y-1.5 md:space-y-2">
                            {agent.limitations.map((limitation, index) => (
                              <li
                                key={index}
                                className="flex items-start gap-2 animate-slide-up"
                                style={{ animationDelay: `${index * 0.1}s` }}
                              >
                                <span className="text-[var(--foreground)] text-lg leading-none">
                                  •
                                </span>
                                <span className="text-[var(--foreground)] -mt-1 text-sm md:text-base">
                                  {limitation}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-[var(--foreground)] italic text-sm md:text-base">
                            No limitations specified
                          </p>
                        )}
                      </div>

                      <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-3 md:p-4 panel-transition hover-scale">
                        <div className="flex flex-col space-y-3">
                          {/* Agent Type */}
                          <div className="flex items-center justify-between">
                            <span className="text-[var(--foreground)] text-xs md:text-sm">
                              Agent Type:
                            </span>
                            <span
                              className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm ${
                                agent?.is_outbound
                                  ? "bg-blue-900 text-blue-200"
                                  : "bg-purple-900 text-purple-200"
                              }`}
                            >
                              {agent?.is_outbound ? "Outbound" : "Inbound"}
                            </span>
                          </div>

                          {/* Languages */}
                          <div className="space-y-2 md:space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[var(--foreground)] text-xs md:text-sm">
                                Main Language:
                              </span>
                              <span className="text-[var(--foreground)] text-xs md:text-sm px-2 py-0.5 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded">
                                {agent &&
                                  getLanguageName(agent.primary_language)}
                              </span>
                            </div>
                            <div>
                              <div className="flex flex-wrap gap-1.5 justify-between">
                                <span className="text-[var(--foreground)] text-xs md:text-sm">
                                  Also speaks:
                                </span>
                                <div className="flex flex-wrap gap-1.5">
                                  {agent &&
                                    agent.supported_languages
                                      .filter(
                                        (lang) =>
                                          lang !== agent.primary_language
                                      )
                                      .map((lang, index) => (
                                        <span
                                          key={index}
                                          className="text-[var(--secondary-foreground)] text-xs px-2 py-1 bg-[var(--secondary)] rounded"
                                        >
                                          {getLanguageName(lang)}
                                        </span>
                                      ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Phone Call Mode UI with back button inside the card */
                  <div className="grid w-full grid-cols-1 gap-4 xl:grid-cols-[minmax(0,28rem)_minmax(0,1fr)]">
                    <div className="w-full bg-[var(--card)] p-6 rounded-lg border border-[var(--border)] shadow-md animate-slide-up">
                      <h3 className="text-2xl md:text-3xl font-display font-medium text-[var(--foreground)] text-center mb-6 flex items-center justify-center gap-2">
                        <HiPhone className="w-8 h-8 text-[var(--secondary)]" />
                        Call Voice Agent
                      </h3>

                      {/* No uses remaining message in phone call mode */}
                      {!isAdmin &&
                        remainingUses !== null &&
                        remainingUses <= 0 && (
                          <div className="bg-amber-500/10 text-amber-500 px-4 py-3 rounded-lg text-sm mb-6 border border-amber-500/20 animate-pulse-slow">
                            <p className="font-medium mb-1 text-center">
                              You have no uses remaining
                            </p>
                            <p className="text-center">
                              Please contact{" "}
                              <a
                                href="mailto:contact@ukti.ai"
                                className="underline font-medium"
                              >
                                contact@ukti.ai
                              </a>{" "}
                              to request more.
                            </p>
                          </div>
                        )}

                      <form
                        onSubmit={handlePhoneCallSubmit}
                        className="space-y-6"
                      >
                        <div className="rounded-xl border border-[var(--border)] bg-gradient-to-br from-[var(--card)] to-[var(--card-container)] p-4 md:p-5 space-y-4">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                              <h3 className="text-sm font-semibold text-[var(--foreground)]">
                                Direct Call Setup
                              </h3>
                              <p className="text-xs text-[var(--foreground-muted)] mt-1">
                                Add runtime details before placing the call.
                              </p>
                            </div>
                            {previousCallValues && (
                              <button
                                type="button"
                                onClick={applyPreviousCallValues}
                                disabled={
                                  isPhoneCallProcessing ||
                                  (!isAdmin &&
                                    remainingUses !== null &&
                                    remainingUses <= 0)
                                }
                                className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm text-[var(--foreground)] bg-[var(--background)] hover:bg-[var(--muted)] transition-colors disabled:opacity-50"
                              >
                                Fill with previous call values
                              </button>
                            )}
                          </div>

                          <div>
                            <label
                              htmlFor="phone"
                              className="block text-sm font-medium text-[var(--foreground)] mb-2"
                            >
                              Customer Phone Number
                            </label>
                            <PhoneInput
                              id="phone"
                              value={phoneNumber}
                              onChange={handlePhoneNumberChange}
                              onBlur={validatePhoneNumber}
                              placeholder={
                                phoneNumberPlaceholder
                                  ? phoneNumberPlaceholder
                                  : "Enter your Indian phone number"
                              }
                              disabled={
                                isPhoneCallProcessing ||
                                (!isAdmin &&
                                  remainingUses !== null &&
                                  remainingUses <= 0)
                              }
                              className={`w-full ${
                                !isAdmin &&
                                remainingUses !== null &&
                                remainingUses <= 0
                                  ? "opacity-60 cursor-not-allowed"
                                  : ""
                              }`}
                              error={!!phoneValidationMsg}
                              autoComplete="tel"
                            />
                            {phoneValidationMsg && (
                              <p className="mt-2 text-sm text-red-500 animate-fade-in">
                                {phoneValidationMsg}
                              </p>
                            )}
                          </div>
                        </div>

                        {phoneDirectFieldKeys.length > 0 ? (
                          <div className="space-y-4">
                            {phoneDirectFieldKeys.map((key, index) => (
                              <div key={key}>
                                <label
                                  htmlFor={`phone_field_${key}`}
                                  className="block text-sm font-medium text-[var(--foreground)] mb-2"
                                >
                                  {key === "bot_name"
                                    ? "Bot name"
                                    : formatRuntimeFieldLabel(key)}
                                  {index > 0 ? " (Optional)" : ""}
                                </label>
                                <input
                                  id={`phone_field_${key}`}
                                  type="text"
                                  value={phoneCallDirectFields[key] ?? ""}
                                  onChange={(e) => {
                                    const nextValue = e.target.value;
                                    resetProviderCheck();
                                    setPhoneCallDirectFields((prev) => ({
                                      ...prev,
                                      [key]: nextValue,
                                    }));
                                  }}
                                  placeholder={
                                    key === "bot_name"
                                      ? "Enter bot name"
                                      : `Enter ${formatRuntimeFieldLabel(
                                          key
                                        ).toLowerCase()}`
                                  }
                                  disabled={
                                    isPhoneCallProcessing ||
                                    (!isAdmin &&
                                      remainingUses !== null &&
                                      remainingUses <= 0)
                                  }
                                  className={`w-full px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--secondary)] ${
                                    !isAdmin &&
                                    remainingUses !== null &&
                                    remainingUses <= 0
                                      ? "opacity-60 cursor-not-allowed"
                                      : ""
                                  }`}
                                  autoComplete={key === "name" ? "name" : "off"}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[var(--foreground-muted)]">
                            No direct runtime placeholders configured for this agent.
                          </p>
                        )}

                        <div className="space-y-3 border border-[var(--border)] rounded-md p-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-[var(--foreground)]">
                              EXTRA INFOS
                            </h4>
                            <button
                              type="button"
                              onClick={() => {
                                resetProviderCheck();
                                setPhoneCallExtraInfos((prev) => [
                                  ...prev,
                                  createRuntimeInfoEntry(),
                                ]);
                              }}
                              disabled={
                                isPhoneCallProcessing ||
                                (!isAdmin &&
                                  remainingUses !== null &&
                                  remainingUses <= 0)
                              }
                              className="px-2 py-1 text-xs rounded border border-[var(--border)] hover:bg-[var(--muted)] transition-colors disabled:opacity-50"
                            >
                              Add
                            </button>
                          </div>
                          {phoneCallExtraInfos.length > 0 ? (
                            <div className="space-y-2">
                              {phoneCallExtraInfos.map((entry) => (
                                <div key={entry.id} className="grid grid-cols-12 gap-2">
                                  <input
                                    type="text"
                                    value={entry.key}
                                    onChange={(e) => {
                                      resetProviderCheck();
                                      setPhoneCallExtraInfos((prev) =>
                                        prev.map((item) =>
                                          item.id === entry.id
                                            ? { ...item, key: e.target.value }
                                            : item
                                        )
                                      );
                                    }}
                                    placeholder="Label"
                                    disabled={
                                      isPhoneCallProcessing ||
                                      (!isAdmin &&
                                        remainingUses !== null &&
                                        remainingUses <= 0)
                                    }
                                    className="col-span-4 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--secondary)]"
                                  />
                                  <input
                                    type="text"
                                    value={entry.value}
                                    onChange={(e) => {
                                      resetProviderCheck();
                                      setPhoneCallExtraInfos((prev) =>
                                        prev.map((item) =>
                                          item.id === entry.id
                                            ? { ...item, value: e.target.value }
                                            : item
                                        )
                                      );
                                    }}
                                    placeholder="Value"
                                    disabled={
                                      isPhoneCallProcessing ||
                                      (!isAdmin &&
                                        remainingUses !== null &&
                                        remainingUses <= 0)
                                    }
                                    className="col-span-7 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--secondary)]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      resetProviderCheck();
                                      setPhoneCallExtraInfos((prev) =>
                                        prev.filter((item) => item.id !== entry.id)
                                      );
                                    }}
                                    disabled={
                                      isPhoneCallProcessing ||
                                      (!isAdmin &&
                                        remainingUses !== null &&
                                        remainingUses <= 0)
                                    }
                                    className="col-span-1 text-xs rounded border border-[var(--border)] hover:bg-[var(--muted)] transition-colors disabled:opacity-50"
                                  >
                                    X
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-[var(--foreground-muted)]">
                              Add key/value pairs to be injected into {"{USER_DETAILS}"}.
                            </p>
                          )}
                        </div>

                        {isAdmin && providerCheck && (
                          <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <h4 className="text-sm font-semibold text-[var(--foreground)]">
                                  Provider latency
                                </h4>
                                {providerCheck.strategy && (
                                  <p className="text-xs text-[var(--foreground-muted)] mt-1">
                                    {providerCheck.strategy}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1 text-xs text-[var(--color-accent)]">
                                <HiCheck className="w-4 h-4" />
                                Informational
                              </div>
                            </div>

                            <div className="space-y-2">
                              {getProviderCheckStages(providerCheck).map((stage) => {
                                const comparison =
                                  providerCheck.comparisons?.[stage];
                                const currentProvider =
                                  comparison?.current_provider ||
                                  providerCheck.providers?.[stage];
                                const selectedProvider =
                                  comparison?.selected_provider ||
                                  providerCheck.selected_providers?.[stage] ||
                                  currentProvider;
                                const changed =
                                  currentProvider &&
                                  selectedProvider &&
                                  currentProvider !== selectedProvider;
                                const latencyReason = getProviderLatencyReason(
                                  stage,
                                  comparison,
                                  currentProvider,
                                  selectedProvider
                                );

                                return (
                                  <div
                                    key={stage}
                                    className="rounded-md border border-[var(--border)] bg-[var(--background)] p-3"
                                  >
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                      <div className="text-xs font-medium text-[var(--foreground-muted)]">
                                        {formatStageLabel(stage)}
                                      </div>
                                      <div className="text-sm text-[var(--foreground)]">
                                        {formatProviderLabel(currentProvider)}
                                        <span className="mx-2 text-[var(--foreground-muted)]">
                                          -&gt;
                                        </span>
                                        <span
                                          className={
                                            changed
                                              ? "font-semibold text-[var(--color-accent)]"
                                              : "font-semibold"
                                          }
                                        >
                                          {formatProviderLabel(selectedProvider)}
                                        </span>
                                      </div>
                                    </div>

                                    {latencyReason && (
                                      <p className="mt-2 text-xs font-medium text-teal-500">
                                        {latencyReason}
                                      </p>
                                    )}

                                    {comparison?.results &&
                                      comparison.results.length > 0 && (
                                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                          {comparison.results.map((result) => (
                                            <div
                                              key={`${stage}-${result.provider}`}
                                              className="rounded border border-[var(--border)] px-2 py-1.5 text-xs"
                                            >
                                              <div className="flex items-center justify-between gap-2">
                                                <span className="font-medium text-[var(--foreground)]">
                                                  {formatProviderLabel(
                                                    result.provider
                                                  )}
                                                </span>
                                                <span
                                                  className={
                                                    result.success
                                                      ? "text-[var(--color-accent)]"
                                                      : "text-red-500"
                                                  }
                                                >
                                                  {result.success
                                                    ? formatProbeLatency(
                                                        result.total_ms
                                                      )
                                                    : "failed"}
                                                </span>
                                              </div>
                                              {!result.success &&
                                                result.error && (
                                                  <p className="mt-1 truncate text-[var(--foreground-muted)]">
                                                    {result.error}
                                                  </p>
                                                )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div>
                          <motion.button
                            type="submit"
                            disabled={
                              isPhoneCallProcessing ||
                              isPhoneCallCoolingDown ||
                              (!isAdmin &&
                                remainingUses !== null &&
                                remainingUses <= 0)
                            }
                            hidden={
                              !isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0
                            }
                            whileHover={{
                              scale:
                                (!isAdmin &&
                                  remainingUses !== null &&
                                  remainingUses <= 0) ||
                                isPhoneCallProcessing ||
                                isPhoneCallCoolingDown
                                  ? 1
                                  : 1.02,
                            }}
                            whileTap={{
                              scale:
                                (!isAdmin &&
                                  remainingUses !== null &&
                                  remainingUses <= 0) ||
                                isPhoneCallProcessing ||
                                isPhoneCallCoolingDown
                                  ? 1
                                  : 0.98,
                            }}
                            className={`w-full bg-[var(--secondary)] text-[var(--secondary-foreground)] px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm md:text-base font-medium ${
                              isPhoneCallProcessing ||
                              isPhoneCallCoolingDown ||
                              (!isAdmin &&
                                remainingUses !== null &&
                                remainingUses <= 0)
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-[var(--secondary-hover)]"
                            }`}
                          >
                            {isPhoneCallProcessing ? (
                              <>
                                <motion.span
                                  animate={{ rotate: 360 }}
                                  transition={{
                                    duration: 1,
                                    repeat: Infinity,
                                    ease: "linear",
                                  }}
                                  className="inline-block w-5 h-5 border-2 border-t-2 border-white/30 border-t-white rounded-full"
                                ></motion.span>
                                Processing...
                              </>
                            ) : isPhoneCallCoolingDown ? (
                              <>
                                <HiPhone className="w-5 h-5" />
                                Wait {phoneCallCooldownRemainingSeconds}s
                              </>
                            ) : (
                              <>
                                {isAdmin ? (
                                  <>
                                    <HiPhone className="w-5 h-5" />
                                    Make direct call
                                  </>
                                ) : (
                                  <>
                                    <HiPhone className="w-5 h-5" />
                                    Call Now
                                  </>
                                )}
                              </>
                            )}
                          </motion.button>
                        </div>
                      </form>

                      {/* Success/Error Message */}
                      {phoneCallMessage && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 25,
                          }}
                          className="mt-6 rounded-lg text-sm overflow-hidden shadow-md font-sans"
                        >
                          <div
                            className={`p-1 ${
                              phoneCallMessage.type === "success"
                                ? "bg-[var(--color-accent)]"
                                : "bg-[var(--destructive)]"
                            }`}
                          ></div>
                          <div
                            className={`p-4 ${
                              phoneCallMessage.type === "success"
                                ? "bg-[var(--card)] text-[var(--foreground)]"
                                : "bg-[var(--card)] text-[var(--foreground)]"
                            }`}
                          >
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                {phoneCallMessage.type === "success" ? (
                                  <motion.div
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{
                                      type: "spring",
                                      stiffness: 400,
                                      damping: 10,
                                      delay: 0.1,
                                    }}
                                    className="w-8 h-8 rounded-full bg-[var(--color-accent)]/20 flex items-center justify-center"
                                  >
                                    <svg
                                      className="w-5 h-5 text-[var(--color-accent)]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  </motion.div>
                                ) : (
                                  <motion.div
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{
                                      type: "spring",
                                      stiffness: 400,
                                      damping: 10,
                                      delay: 0.1,
                                    }}
                                    className="w-8 h-8 rounded-full bg-[var(--destructive)]/20 flex items-center justify-center"
                                  >
                                    <svg
                                      className="w-5 h-5 text-[var(--destructive)]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </motion.div>
                                )}
                              </div>
                              <div>
                                <h3 className="font-display font-medium mb-1 text-[var(--foreground)]">
                                  {phoneCallMessage.title ||
                                    (phoneCallMessage.type === "success"
                                      ? "Call Initiated Successfully"
                                      : "Call Initiation Failed")}
                                </h3>
                                <p className="text-[var(--foreground-muted)]">
                                  {phoneCallMessage.text}
                                </p>

                                {phoneCallMessage.type === "success" && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    transition={{ delay: 0.3 }}
                                    className="mt-2 pt-2 border-t border-[var(--border)]"
                                  >
                                    <div className="flex items-center text-xs text-[var(--foreground-muted)]">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1 text-[var(--color-accent)]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      <span>
                                        You should receive a call shortly
                                      </span>
                                    </div>
                                    <div className="flex items-center text-xs mt-1 text-[var(--foreground-muted)]">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1 text-[var(--color-accent)]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      <span>
                                        Keep your phone ready to answer
                                      </span>
                                    </div>
                                  </motion.div>
                                )}

                                {phoneCallMessage.type === "error" && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    transition={{ delay: 0.3 }}
                                    className="mt-2 pt-2 border-t border-[var(--border)]"
                                  >
                                    <button
                                      onClick={() => {
                                        setPhoneCallMessage(null);
                                        // Focus on the phone input after a short delay
                                        setTimeout(() => {
                                          const phoneInput =
                                            document.getElementById("phone");
                                          if (phoneInput) phoneInput.focus();
                                        }, 100);
                                      }}
                                      className="text-xs px-3 py-1 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded transition-colors hover:bg-[var(--secondary-hover)]"
                                    >
                                      Try Again
                                    </button>
                                  </motion.div>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Info note - enhanced */}
                      <div className="mt-6 text-sm text-[var(--foreground)]/70 p-4 border border-[var(--border)]/40 rounded-lg bg-[var(--background)]/50">
                        <p className="flex items-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-[var(--accent)]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          The agent will call you at the phone number provided.
                          Standard call charges may apply.
                        </p>
                      </div>
                    </div>
                    {isAdmin && <DirectCallTracePanel />}
                  </div>
                )}
              </div>
            </div>

            {/* Live Chat Panel - Only show when not in phone call mode */}
            {!phoneCallMode && showChat && (
              <div
                className={`bg-[var(--card-container)] border border-[var(--border)] shadow-lg transition-all duration-500 panel-transition animate-fade-in
                  ${
                    isMobile
                      ? "fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl transform transition-transform duration-300"
                      : "lg:w-96 w-full lg:static rounded-3xl"
                  }`}
                style={{
                  transform:
                    isMobile && chatExpanded
                      ? "translateY(0)"
                      : isMobile && !chatExpanded
                      ? "translateY(85%)"
                      : "none",
                  maxHeight:
                    isMobile && chatExpanded
                      ? "80vh"
                      : isMobile && !chatExpanded
                      ? "30vh"
                      : "auto",
                }}
                onTouchStart={isMobile ? handleTouchStart : undefined}
                onTouchMove={isMobile ? handleTouchMove : undefined}
                onTouchEnd={isMobile ? handleTouchEnd : undefined}
              >
                {/* Chat header */}
                <div className="flex items-center justify-between p-3 border-b border-[var(--border)]">
                  <h3 className="text-[var(--foreground)] font-medium text-base">
                    Live Chat
                  </h3>

                  {/* Swipe indicator - only on mobile */}
                  {isMobile && (
                    <div
                      className="absolute left-1/2 transform -translate-x-1/2 top-0 w-full flex flex-col items-center justify-center pt-3 pb-2 cursor-pointer"
                      onClick={() => setChatExpanded(!chatExpanded)}
                    >
                      <div className="h-1 w-16 bg-gray-300 rounded-full mb-1"></div>
                      <div
                        className={`transition-transform duration-300 ${
                          chatExpanded ? "rotate-180" : ""
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-400 animate-arrow-bounce"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {/* Only show close button on mobile */}
                    {isMobile && (
                      <button
                        onClick={() => setShowChat(false)}
                        className="text-[var(--foreground)] hover:text-[var(--foreground)] transition-colors p-1 button-transition"
                      >
                        <IconWrapper icon={IoMdClose} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Chat messages container */}
                <div
                  ref={chatContainerRef}
                  className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-3 md:space-y-4 
                    min-h-[250px] max-h-[350px] lg:min-h-[350px] lg:max-h-[500px]"
                >
                  {chatMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 animate-fade-in">
                      <HiOutlineChat className="w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 text-[var(--foreground)] animate-bounce-slow" />
                      <p className="text-[var(--foreground)] text-sm md:text-base">
                        No messages yet
                      </p>
                      <p className="text-xs md:text-sm mt-2 text-[var(--foreground)]">
                        Start speaking to see the chat history
                      </p>
                    </div>
                  ) : (
                    chatMessages.map((message, index) =>
                      message.type === "user" ? (
                        <div
                          key={index}
                          className="bg-[var(--color-secondary)] p-3 rounded-lg shadow-sm max-w-[85%] text-[var(--secondary-foreground)] animate-slide-up"
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className="font-medium text-xs md:text-sm mb-1 flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="bg-[var(--background)] p-1 rounded-full mr-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3 md:h-3.5 md:w-3.5 text-[var(--color-secondary)]"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                              </span>
                              <span>You</span>
                            </div>
                            <span className="text-xs opacity-80">
                              {formatMessageTime(message.timestamp)}
                            </span>
                          </div>
                          <div className="font-medium text-sm md:text-base">
                            {message.text}
                          </div>
                        </div>
                      ) : (
                        <div
                          key={index}
                          className="bg-[var(--card)] p-3 rounded-lg ml-auto mr-0 shadow-sm max-w-[85%] text-[var(--card-foreground)] animate-slide-up"
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className="font-medium text-xs md:text-sm mb-1 flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="bg-[var(--background)] p-1 rounded-full mr-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3 md:h-3.5 md:w-3.5 text-[var(--foreground)]"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                  />
                                </svg>
                              </span>
                              <span>Agent</span>
                            </div>
                            <span className="text-xs opacity-80">
                              {formatMessageTime(message.timestamp)}
                            </span>
                          </div>
                          <div className="font-medium text-sm md:text-base">
                            {message.text}
                          </div>
                        </div>
                      )
                    )
                  )}
                </div>
              </div>
            )}

            {/* Chat toggle button - only on mobile and only when chat is closed and not in phone call mode */}
            {!showChat && isMobile && !phoneCallMode && (
              <button
                onClick={() => {
                  setShowChat(true);
                  setChatExpanded(true); // Open directly to maximized state
                }}
                className="fixed bottom-4 right-4 z-50 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-[var(--secondary-hover)] transition-colors button-transition animate-pulse-slow"
              >
                <HiOutlineChat className="w-6 h-6" />
                {chatMessages.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold animate-bounce-slow">
                    {chatMessages.length > 9 ? "9+" : chatMessages.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Cost Breakdown Modal */}
          {isAdmin && showCostModal && (
            <Modal
              isOpen={showCostModal}
              onClose={() => setShowCostModal(false)}
              title="Call Cost Breakdown"
            >
              <div className="bg-[var(--card)] text-[var(--foreground)] rounded-lg p-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                <div className="w-full max-w-3xl mx-auto">
                  <div className="bg-[var(--card)] p-4 rounded-lg border border-[var(--border)] mb-6">
                    <h3 className="text-[var(--foreground)] font-semibold mb-3">
                      Session Info
                    </h3>
                    <div className="flex justify-between text-sm">
                      <p className="text-[var(--foreground)]">Duration: </p>
                      <SessionDurationDisplay />
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <p className="text-[var(--foreground)]">Total Tokens: </p>
                      <p className="text-[var(--foreground)] font-medium">
                        {(
                          tokenUsage.input_tokens + tokenUsage.output_tokens
                        ).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <p className="text-[var(--foreground)]">
                        Per Minute Cost:{" "}
                      </p>
                      <p className="text-[var(--foreground)] font-medium">
                        ${calculateDetailedCosts().perMinuteUsdCost.toFixed(4)}{" "}
                        / ₹
                        {calculateDetailedCosts().perMinuteInrCost.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-[var(--card)] p-4 rounded-lg border border-[var(--border)] mb-6">
                    <h3 className="text-[var(--foreground)] font-semibold mb-3">
                      Token Usage Summary
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-[var(--foreground)]">
                          Input Tokens:{" "}
                          <span className="text-[var(--foreground)] font-medium">
                            {tokenUsage.input_tokens.toLocaleString()}
                          </span>
                        </p>
                        <div className="ml-4 mt-1">
                          <p className="text-[var(--foreground)]">
                            Text:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.text || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)]">
                            Audio:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.audio || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)]">
                            Cached:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.cached || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)] ml-4">
                            Cached Text:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.cached_text || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)] ml-4">
                            Cached Audio:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.cached_audio || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-[var(--foreground)]">
                          Output Tokens:{" "}
                          <span className="text-[var(--foreground)] font-medium">
                            {tokenUsage.output_tokens.toLocaleString()}
                          </span>
                        </p>
                        <div className="ml-4 mt-1">
                          <p className="text-[var(--foreground)]">
                            Text:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.output_token_details.text || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)]">
                            Audio:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.output_token_details.audio || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[var(--card)] p-4 rounded-lg border border-[var(--border)] mb-6">
                    <h3 className="text-[var(--foreground)] font-semibold mb-3">
                      Cost Breakdown
                    </h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left pb-2 font-medium text-gray-600">
                            Type
                          </th>
                          <th className="text-right pb-2 font-medium text-gray-600">
                            Rate
                          </th>
                          <th className="text-right pb-2 font-medium text-gray-600">
                            Tokens/Units
                          </th>
                          <th className="text-right pb-2 font-medium text-gray-600">
                            Cost
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Input Text</td>
                          <td className="py-2 text-right text-gray-700">
                            ${COST_CONSTANTS.GEMINI_INPUT_TEXT.toFixed(2)}/1M
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {(
                              tokenUsage.input_token_details.text || 0
                            ).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ${calculateDetailedCosts().textInputCost.toFixed(4)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Input Audio</td>
                          <td className="py-2 text-right text-gray-700">
                            ${COST_CONSTANTS.GEMINI_INPUT_AUDIO.toFixed(2)}/1M
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {(
                              tokenUsage.input_token_details.audio || 0
                            ).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            $
                            {calculateDetailedCosts().audioInputCost.toFixed(4)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Output Text</td>
                          <td className="py-2 text-right text-gray-700">
                            ${COST_CONSTANTS.GEMINI_OUTPUT_TEXT.toFixed(2)}/1M
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {(
                              tokenUsage.output_token_details.text || 0
                            ).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            $
                            {calculateDetailedCosts().textOutputCost.toFixed(4)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Output Audio</td>
                          <td className="py-2 text-right text-gray-700">
                            ${COST_CONSTANTS.GEMINI_OUTPUT_AUDIO.toFixed(2)}/1M
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {(
                              tokenUsage.output_token_details.audio || 0
                            ).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            $
                            {calculateDetailedCosts().audioOutputCost.toFixed(
                              4
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">
                            Fixed Connection Cost
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ₹{COST_CONSTANTS.FIXED_CONNECTION_COST.toFixed(2)}
                          </td>
                          <td className="py-2 text-right text-gray-700">1</td>
                          <td className="py-2 text-right text-gray-700">
                            ₹{calculateDetailedCosts().fixedCost.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">
                            Calling Service
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ₹
                            {COST_CONSTANTS.CALLING_SERVICE_COST_PER_MIN.toFixed(
                              2
                            )}
                            /min
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {callDurationMinutes} min
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ₹
                            {calculateDetailedCosts().callingServiceCost.toFixed(
                              2
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Service Charge</td>
                          <td className="py-2 text-right text-gray-700">
                            ₹{COST_CONSTANTS.SERVICE_CHARGE_PER_MIN.toFixed(2)}
                            /min
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {callDurationMinutes} min
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ₹{calculateDetailedCosts().serviceCharge.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-gray-200">
                          <td
                            colSpan={3}
                            className="pt-3 text-gray-900 font-medium"
                          >
                            API Cost (USD)
                          </td>
                          <td className="pt-3 text-right text-gray-900 font-medium">
                            ${calculateDetailedCosts().totalUsdCost.toFixed(4)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} className="text-gray-900 font-medium">
                            API Cost (INR) @ ₹{COST_CONSTANTS.USD_TO_INR}/USD
                          </td>
                          <td className="text-right text-gray-900 font-medium">
                            ₹{calculateDetailedCosts().totalInrCost.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="border-t-2 border-gray-300">
                          <td
                            colSpan={3}
                            className="pt-3 text-gray-900 font-bold"
                          >
                            Grand Total (INR)
                          </td>
                          <td className="pt-3 text-right text-gray-900 font-bold">
                            ₹{calculateDetailedCosts().grandTotalInr.toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="text-xs text-gray-500 mb-4">
                    <p className="mb-1">Notes:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>
                        Costs are calculated based on current pricing for
                        Ukti.ai
                      </li>
                      <li>
                        Fixed connection cost (₹
                        {COST_CONSTANTS.FIXED_CONNECTION_COST}) is applied once
                        per session
                      </li>
                      <li>
                        Calling service charges (₹
                        {COST_CONSTANTS.CALLING_SERVICE_COST_PER_MIN}/min) are
                        billed by rounding up to the next minute
                      </li>
                      <li>
                        Service charge (₹{COST_CONSTANTS.SERVICE_CHARGE_PER_MIN}
                        /min) is applied for each minute
                      </li>
                      <li>
                        USD to INR conversion fixed at rate of ₹
                        {COST_CONSTANTS.USD_TO_INR}/USD
                      </li>
                      <li>
                        This is a live counter that updates with each agent
                        response
                      </li>
                    </ul>
                  </div>

                  <div className="mt-6 flex justify-end gap-4">
                    <button
                      onClick={resetCostCounter}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Reset Counter
                    </button>
                    <button
                      onClick={() => setShowCostModal(false)}
                      className="px-4 py-2 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded hover:bg-[var(--secondary-hover)] transition-colors flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </div>
      <audio ref={audioRef} autoPlay playsInline style={{ display: "none" }} />

      {showBrowserTryModal && (
        <Modal
          isOpen={showBrowserTryModal}
          onClose={handleBrowserTryModalClose}
          title="Web Call Setup"
          size="md"
        >
          <form onSubmit={handleBrowserTryConnect} className="space-y-6 p-5">
            <div>
              <h3 className="mb-3 text-sm font-semibold text-[var(--foreground)]">
                Call Data
              </h3>
              {browserDirectFieldKeys.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {browserDirectFieldKeys.map((key, index) => (
                    <div key={key}>
                      <label
                        htmlFor={`browser_field_${key}`}
                        className="mb-1.5 block text-xs font-medium text-[var(--foreground-muted)]"
                      >
                        {formatRuntimeFieldLabel(key)}
                        {index > 0 ? " (Optional)" : ""}
                      </label>
                      <input
                        id={`browser_field_${key}`}
                        type="text"
                        value={browserDirectFields[key] ?? ""}
                        onChange={(event) =>
                          setBrowserDirectFields((previous) => ({
                            ...previous,
                            [key]: event.target.value,
                          }))
                        }
                        disabled={isBrowserTryStarting || isConnecting}
                        className="h-10 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] outline-none focus:border-[var(--secondary)] disabled:opacity-60"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--foreground-muted)]">
                  This agent has no configured call fields. Add extra data below
                  when needed.
                </p>
              )}
            </div>

            <div className="border-t border-[var(--border)] pt-5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-[var(--foreground)]">
                  Extra Information
                </h3>
                <button
                  type="button"
                  onClick={() =>
                    setBrowserExtraInfos((previous) => [
                      ...previous,
                      createRuntimeInfoEntry(),
                    ])
                  }
                  disabled={isBrowserTryStarting || isConnecting}
                  className="rounded-md border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--foreground)] hover:bg-[var(--muted)] disabled:opacity-60"
                >
                  Add Field
                </button>
              </div>

              <div className="space-y-2">
                {browserExtraInfos.map((entry) => (
                  <div
                    key={entry.id}
                    className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.5fr_auto]"
                  >
                    <input
                      type="text"
                      value={entry.key}
                      onChange={(event) =>
                        setBrowserExtraInfos((previous) =>
                          previous.map((item) =>
                            item.id === entry.id
                              ? { ...item, key: event.target.value }
                              : item
                          )
                        )
                      }
                      placeholder="Field name"
                      disabled={isBrowserTryStarting || isConnecting}
                      className="h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] outline-none focus:border-[var(--secondary)] disabled:opacity-60"
                    />
                    <input
                      type="text"
                      value={entry.value}
                      onChange={(event) =>
                        setBrowserExtraInfos((previous) =>
                          previous.map((item) =>
                            item.id === entry.id
                              ? { ...item, value: event.target.value }
                              : item
                          )
                        )
                      }
                      placeholder="Value"
                      disabled={isBrowserTryStarting || isConnecting}
                      className="h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] outline-none focus:border-[var(--secondary)] disabled:opacity-60"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setBrowserExtraInfos((previous) =>
                          previous.filter((item) => item.id !== entry.id)
                        )
                      }
                      disabled={isBrowserTryStarting || isConnecting}
                      className="h-10 rounded-md border border-red-400/40 px-3 text-xs font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-60"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-[var(--border)] pt-4">
              <button
                type="button"
                onClick={handleBrowserTryModalClose}
                disabled={isBrowserTryStarting || isConnecting}
                className="rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)] disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isBrowserTryStarting || isConnecting}
                className="rounded-md bg-[var(--secondary)] px-4 py-2 text-sm font-medium text-[var(--secondary-foreground)] hover:bg-[var(--secondary-hover)] disabled:opacity-60"
              >
                {isBrowserTryStarting || isConnecting
                  ? "Starting..."
                  : "Start Web Call"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Phone Call Warning */}
      {showPhoneCallWarning && (
        <Modal
          isOpen={showPhoneCallWarning}
          onClose={() => setShowPhoneCallWarning(false)}
          title="Phone Call Warning"
        >
          <div className="p-4">
            <p className="mb-4">
              This will initiate a phone call to the number you provide.
              Standard call charges may apply.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowPhoneCallWarning(false)}
                className="px-4 py-2 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded"
              >
                OK
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Out of Uses Modal */}
      {showOutOfUsesModal && (
        <Modal
          isOpen={showOutOfUsesModal}
          onClose={() => {
            setShowOutOfUsesModal(false);
            // Redirect after closing the modal
            if (onBackToSelection) {
              onBackToSelection();
            } else {
              navigate("/");
            }
          }}
          title="Out of Uses"
        >
          <div className="p-6 text-center">
            <div className="mb-4 flex justify-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-amber-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-medium mb-4">You're out of uses</h3>
            <p className="text-[var(--foreground-muted)] mb-6">
              You have used all your allocated interactions with this voice
              agent. Please contact{" "}
              <a
                href="mailto:contact@ukti.ai"
                className="text-[var(--accent)] font-medium"
              >
                contact@ukti.ai
              </a>{" "}
              to request more.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowOutOfUsesModal(false);
                  // Redirect after closing the modal
                  if (onBackToSelection) {
                    onBackToSelection();
                  } else {
                    navigate("/");
                  }
                }}
                className="px-6 py-2.5 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded-md font-medium"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Custom scrollbar styles */}
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: var(--card-container);
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: var(--border);
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: var(--color-secondary);
          }

          /* New animations */
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideUp {
            from { transform: translateY(10px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(var(--accent-rgb), 0.4); }
            70% { box-shadow: 0 0 0 10px rgba(var(--accent-rgb), 0); }
            100% { box-shadow: 0 0 0 0 rgba(var(--accent-rgb), 0); }
          }
          
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }

          @keyframes arrowBounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
          }

          .animate-arrow-bounce {
            animation: arrowBounce 1.5s ease-in-out infinite;
          }

          .animate-fade-in {
            animation: fadeIn 0.3s ease-in-out;
          }

          .animate-slide-up {
            animation: slideUp 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28);
            transform-origin: bottom left;
          }

          .animate-pulse-slow {
            animation: pulse 2s infinite;
          }
          
          .animate-bounce-slow {
            animation: bounce 2s infinite;
          }

          .hover-scale {
            transition: transform 0.2s ease-out;
          }
          
          .hover-scale:hover {
            transform: scale(1.02);
          }

          .panel-transition {
            transition: all 0.3s ease-out;
          }
          
          .button-transition {
            transition: all 0.15s ease-out;
          }
          
          .button-transition:hover {
            transform: translateY(-1px);
            filter: brightness(1.05);
          }

          .chat-message-transition {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          /* Visualizer effects */
          .active-visualizer {
            transition: transform 0.3s ease-out;
          }
          
          .visualizer-glow {
            animation: glow 3s ease-in-out infinite alternate;
            border-radius: 50%;
          }
          
          @keyframes glow {
            0% {
              box-shadow: 0 0 15px 5px rgba(var(--accent-rgb), 0.15);
            }
            100% {
              box-shadow: 0 0 25px 10px rgba(var(--accent-rgb), 0.25);
            }
          }
          
          /* Add smooth entry animation for the visualizer */
          .active-visualizer {
            animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          
          @keyframes scaleIn {
            0% {
              transform: scale(0.95);
              opacity: 0.7;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default AudioAgent;
